// 画面の HTML を作る。サーバー（最初の表示）と画面側（そのあとの描き直し）の両方で同じものを使う
import type { Camp, Page, Post, SearchResult, TopicSummary, TopicView, Viewer, MyPost, InboxItem, ArenaView, ArenaStatus, ArenaWait, LiveArena, Slot, CaseView, ModLogEntry, ContactView, AdminView, AdminUser, AdminStats, AdminReported, AdminDecided, AdminInvite, AdminUserDetail, DebateView, DebateDraft, LiveDebate, TrophySummary, TrophyView } from './types';
import { TIER_NAME } from './trophy';
import { DEBATE_STEPS, DEBATE_MAX, DEBATE_RULE, WAIT_CHOICES } from './debate';
import {
  NONE, DAY, NEW_LIMIT, NEW_VIEW_TARGET, DISPUTE_THRESHOLD, PROPOSAL_THRESHOLD,
  HANDLE_MAX, TITLE_MAX, CAMP_MAX, SOURCE_MAX, WAIT_HOURS, REPORT_REASONS, URGENT_REASONS, hostOf, remain, waitLabel, esc, ago, cut, score, NARUHODO_WEIGHT, seededOrder, sortTags, PRESET_TAGS, dateLabel,
} from './util';

// 見る人の操作で変わる表示の状態（データベースには残さない）
export interface Ui {
  filter: string;                    // 話題の画面の絞り込み（all / 立場ID / none / info）
  order: 'rec' | 'new' | 'like';
  stance: string;                    // 投稿欄で選んでいる立場
  openFolded: Set<string>;
  query: string;                     // トップの検索欄
  newTags: string[];                 // 話題を立てる画面で選んだタグ
  newFirst: string;
  revealed: Set<string>;             // 「表示する」を押した、印の付いた話題
}
export function freshUi(): Ui {
  return { filter: 'all', order: 'rec', stance: '', openFolded: new Set(), query: '', newTags: [], newFirst: '', revealed: new Set() };
}

const SHARE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12M7 8l5-5 5 5M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const MORE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="6" cy="12" r="1.6" fill="currentColor"/><circle cx="12" cy="12" r="1.6" fill="currentColor"/><circle cx="18" cy="12" r="1.6" fill="currentColor"/></svg>';

// ---------- 根拠のリンク ----------
// 表示：本文の下に「根拠：nhk.or.jp」のようにサイト名で出す
export function sourcesHtml(list: string[]): string {
  if (!list.length) return '';
  return `<div class="sources"><span class="sources-label">根拠</span>${list.map(u =>
    `<a href="${esc(u)}" target="_blank" rel="nofollow noopener noreferrer ugc" title="${esc(u)}">${esc(hostOf(u))}</a>`).join('')}</div>`;
}
// 入力：最初は「根拠のリンクを付ける」だけを出し、押すと欄が開く（open なら最初から開く）
export function sourceInputs(values: string[] = [], open = false): string {
  const show = open || values.length > 0;
  return `
    <div class="source-field">
      <button type="button" class="link small source-toggle" data-source-toggle ${show ? 'hidden' : ''}>＋ 根拠のリンクを追加</button>
      <div class="source-box" ${show ? '' : 'hidden'}>
        ${open ? '' : '<button type="button" class="link small source-close" data-source-close>× 根拠を削除</button>'}
        ${Array.from({ length: SOURCE_MAX }, (_, i) =>
          `<input type="url" class="source-input" inputmode="url" placeholder="${i === 0 ? '根拠のリンク（https://…）' : '2つ目の根拠のリンク（任意）'}" value="${esc(values[i] || '')}">`).join('')}
      </div>
    </div>`;
}

// ---------- 広告の枠 ----------
// 読む流れのじゃまにならない所だけ：パソコンは右の欄の一番下、スマホはページの一番下。
// ひとことの並びの途中、投稿欄のそば、議論場・討論場の中には置かない。ADS が '1' になるまでは何も出さない
// 広告の場所。広告を出していない間も、同じ大きさの場所を取っておく（あとで入れても画面がずれないように）
export function adSlot(on: boolean, where: 'side' | 'foot'): string {
  if (!on) return `<div class="spc-slot spc-${where} spc-empty"><div class="spc-body"><span>将来、ここに広告を表示する場合があります</span></div></div>`;
  return `<div class="spc-slot spc-${where}" aria-label="広告"><span class="spc-label">広告</span><div class="spc-body" data-ad="${where}"></div></div>`;
}

// ---------- 全体の枠 ----------
// ページの一番下と同じリンク。下までたどり着かなくても、右の欄（パソコン）とマイページ（スマホ）から開ける
const SITE_LINKS = `<a href="/guide">使い方</a><a href="/faq">ありそうな質問</a><a href="/terms">利用規約</a><a href="/privacy">プライバシーポリシー</a><a href="/moderation">判定の記録</a><a href="/contact">お問い合わせ</a><a href="https://x.com/risuiGIRONBA" target="_blank" rel="noopener noreferrer">X</a>`;
// 画面の色の設定（マイページ）
export function themeSeg(): string {
  return `<div class="seg theme-seg" role="group" aria-label="画面の色">
    <button data-theme-set="">端末に合わせる</button><button data-theme-set="light">ライト</button><button data-theme-set="dark">ダーク</button>
  </div>`;
}
const sideLinks = () => `<nav class="side-links">${SITE_LINKS}<span>© GIRONBA</span></nav>`;
// 右上の切り替え。押すたびに 自動 → ライト → ダーク → 自動 と変わる。いまの設定のマークを出す
const THEME_BTN = `<button class="theme-btn" data-theme-toggle aria-label="画面の色を切り替える（自動・ライト・ダーク）" title="画面の色：自動・ライト・ダーク">
  <svg class="i-auto" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="2"/><path fill="currentColor" d="M12 4a8 8 0 0 1 0 16z"/></svg>
  <svg class="i-sun" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="12" cy="12" r="4.2" fill="currentColor"/><g stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4"/></g></svg>
  <svg class="i-moon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z"/></svg>
</button>`;

// サイトそのものを共有するアイコン（帯の、画面の色のボタンの隣）
const SHARE_BTN = `<a href="/" class="theme-btn share-btn" data-site-share aria-label="GIRONBA を共有" title="GIRONBA を共有">
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15V3.5M7.5 8 12 3.5 16.5 8"/><path d="M5 12.5V19a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 19v-6.5"/></svg>
</a>`;

// パソコン（幅が広いとき）の左の案内。スマホでは出さず、上の帯（header）を使う
const NAV_ICON = {
  home: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 11l8-7 8 7v9a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>',
  inbox: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15zM10 20a2 2 0 0 0 4 0" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>',
  me: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M4 21a8 8 0 0 1 16 0" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  guide: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .8-1 1.5v.7M12 17h.01" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
};
export function sideNav(v: Viewer): string {
  const item = (href: string, key: keyof typeof NAV_ICON, label: string, extra = '') =>
    `<a href="${href}" data-nav="${key}">${NAV_ICON[key]}<span>${label}</span>${extra}</a>`;
  return `
    <aside class="side-nav">
      <div class="side-top">
        <a href="/" class="logo side-logo" aria-label="GIRONBA"><span class="logo-giron">GIRON</span>BA</a>
        <div class="side-tools">${SHARE_BTN}${THEME_BTN}</div>
      </div>
      <nav class="side-main">
        ${item('/', 'home', 'ホーム')}
        ${v.loggedIn ? item('/inbox', 'inbox', 'お知らせ', v.unread ? `<span class="count-badge">${v.unread}</span>` : '') + item('/me', 'me', 'マイページ') : ''}
        ${item('/guide', 'guide', '使い方')}
      </nav>
      <a class="side-new" href="/new">＋ 話題を立てる</a>
      <!-- 下の段：規約などの小さなリンクと、ログインしていなければログイン・新規登録（パソコンでは一番下のリンクの並びを出さないので、ここからたどれるように） -->
      <div class="side-bottom">
        <nav class="side-small"><a href="/terms">利用規約</a><a href="/privacy">プライバシーポリシー</a><a href="/moderation">判定の記録</a><a href="/contact">お問い合わせ</a></nav>
        <a class="side-x" href="https://x.com/risuiGIRONBA" target="_blank" rel="noopener noreferrer">運営の X：@risuiGIRONBA</a>
        ${v.loggedIn ? '' : '<div class="side-auth"><a href="/login" data-login>ログイン</a><a class="signup-link" href="/signup">新規登録</a></div>'}
      </div>
    </aside>`;
}

export function header(v: Viewer): string {
  return `
    <header class="site">
      <a href="/" class="logo" aria-label="GIRONBA"><span class="logo-giron">GIRON</span>BA</a>
      <nav class="header-nav">
        <span class="header-icons">${SHARE_BTN}${THEME_BTN}</span>
        <a href="/guide" class="header-guide">使い方</a>
        ${v.loggedIn
          ? `<a href="/inbox">お知らせ<span id="inbox-count" class="count-badge" ${v.unread ? '' : 'hidden'}>${v.unread || ''}</span></a>
             <a href="/me">マイページ</a>`
          : '<a href="/login" data-login>ログイン</a><a class="signup-link" href="/signup">新規登録</a>'}
      </nav>
    </header>`;
}

export function renderPage(page: Page, ui: Ui): string {
  const d = page.data, v = page.viewer;
  switch (d.kind) {
    case 'list': return listPage(d.topics, d.tag, d.sort, ui, v.loggedIn, page.ads, d.live, d.debates);
    case 'arena': return arenaPage(d.arena, v, ui);
    case 'debate': return debatePage(d.debate, v);
    case 'topic': return topicPage(d.topic, v, ui, page.ads);
    case 'new': return newPage(d.tags, d.canCreate, d.dailyLimit, ui, page.siteKey);
    case 'me': return mePage(d.handle, d.handleChangeableAt, d.posts, v.isAdmin, d.email, d.inbox, v.isGuest, d.trophy);
    case 'trophies': return trophyPage(d.view, d.tab);
    case 'myposts': return single(`${backHeader('自分の書き込み', '', '/me')}<div class="me-section">${myPostGroups(d.posts) || '<div class="muted small">まだ書き込みはありません</div>'}</div>`);
    case 'inbox': return inboxPage(d.items, d.fromMe);
    case 'login': return loginPage(d.mode, d.next, d.error, d.googleEnabled, d.inviteOnly, page.siteKey);
    case 'welcome': return welcomePage(d.inviteOnly, d.error, d.via, page.siteKey, !!d.direct);
    case 'message': return messagePage(d.title, d.body, d.link, d.linkText);
    case 'doc': return single(`${backHeader(esc(d.title))}<div class="doc">${d.html}</div>`);
    case 'case': return casePage(d.case);
    case 'modlog': return modLogPage(d.entries);
    case 'admin': return adminPage(d.view);
    case 'contact': return contactPage(d.loggedIn, d.categories, page.siteKey);
    default: return messagePage('ページが見つかりません', 'アドレスが間違っているか、ページが削除された可能性があります。', '/', 'トップに戻る');
  }
}

function backHeader(title: string, sub = '', back = '/'): string {
  return `<div class="tl-header"><a href="${back}" class="back-btn" aria-label="戻る">←</a>
    <div><div class="tl-title">${title}</div>${sub ? `<div class="tl-sub">${sub}</div>` : ''}</div></div>`;
}
function single(body: string): string {
  return `<div class="x-layout"><div class="timeline">${body}</div></div>`;
}

// ---------- トップページ ----------
// 話題の印の小さな表示
export const SENS_LABEL = ['', 'センシティブ', '成人向け'];
const sensTag = (level: number) => level ? `<span class="sens-tag">${SENS_LABEL[level]}</span>` : '';

export function topicRow(t: TopicSummary): string {
  return `
    <a class="topic-row" href="/t/${t.id}">
      <div class="topic-title">${esc(t.title)}${sensTag(t.sensitivity)}</div>
      ${t.voices.some(x => x.text) ? `<div class="voices">${t.voices.map(x => `
        <div class="voice">
          <span class="voice-camp">${esc(x.campShort)}</span>
          <span class="voice-text">${x.text ? esc(x.text) : '<span class="muted">まだ「ひとこと」がありません</span>'}</span>
        </div>`).join('')}
      </div>` : `<div class="voices-empty">${t.voices.map(x => `<span class="voice-camp">${esc(x.campShort)}</span>`).join('<span class="muted">・</span>')}<span class="muted">　まだ「ひとこと」がありません</span></div>`}
    </a>`;
}

export function listPage(topics: TopicSummary[], tag: string | null, sort: 'hot' | 'new', ui: Ui, loggedIn = true, ads = false, live: LiveArena[] = [], debates: LiveDebate[] = []): string {
  return `
    <div class="x-layout">
      <div class="timeline">
        ${tag ? `<div class="tl-header tag-head"><a href="/" class="back-btn" aria-label="すべての話題に戻る">←</a><div class="tl-title">#${esc(tag)}</div></div>` : ''}
        <div class="search-row">
          <input type="search" id="search" placeholder="話題・タグ・ひとことを検索" value="${esc(ui.query)}">
          <a class="new-topic-btn" href="/new">＋ 立てる</a>
        </div>
        ${loggedIn ? '' : '<a class="intro-link" href="/guide">はじめての方へ：GIRONBA の使い方 ›</a>'}
        ${live.length || debates.length ? `<div class="live-strip mobile-only">${debates.slice(0, 1).map(x => `
          <a class="arena-link live-item" href="/t/${x.topicId}/d/${x.id}">
            <span class="live">討論場${KIND_DEBATE}</span><span class="live-text">${esc(cut(x.topicTitle, 22))}<span class="muted small">・${esc(x.label)}</span></span>
          </a>`).join('')}${live.slice(0, 1).map(x => `
          <a class="arena-link live-item" href="/t/${x.topicId}/a/${x.id}">
            <span class="live">議論場${KIND_ARENA}</span><span class="live-text">${esc(cut(x.rootText, 26))}<span class="muted small">・${x.recruitCamp ? '交代を募集中' : '議論中'}</span></span>
          </a>`).join('')}</div>` : ''}
        <div id="list-body">${ui.query ? '<p class="empty">検索しています…</p>' : listBody(topics, tag, sort)}</div>
      </div>
      <aside class="sidebar">
        <section class="card"><h2>討論場${KIND_DEBATE}</h2>
          ${debates.map(x => `
            <a class="card-row arena-link" href="/t/${x.topicId}/d/${x.id}">
              <div class="claim">${esc(cut(x.topicTitle, 30))}</div>
              <div class="sub">第${x.no}回・${esc(x.label)}</div>
            </a>`).join('') || '<div class="card-row muted small">現在はありません</div>'}
        </section>
        <section class="card"><h2>議論場${KIND_ARENA}</h2>
          ${live.map(x => `
            <a class="card-row arena-link" href="/t/${x.topicId}/a/${x.id}">
              <div class="claim">${esc(cut(x.rootText, 40))}</div>
              <div class="sub">${esc(x.topicTitle)}・${ago(x.lastAt)}</div>
              ${x.recruitCamp ? `<div class="recruit-tag">「${esc(cut(x.recruitCamp, 12))}」側の交代を募集中</div>` : ''}
            </a>`).join('') || '<div class="card-row muted small">現在はありません</div>'}
        </section>
        ${adSlot(ads, 'side')}
        ${sideLinks()}
      </aside>
    </div>`;
}

export function listBody(topics: TopicSummary[], tag: string | null, sort: 'hot' | 'new'): string {
  const base = tag ? `/tag/${encodeURIComponent(tag)}` : '/';
  return `
    <div class="tabs">
      <a class="tab ${sort !== 'new' ? 'on' : ''}" href="${base}"><span>盛り上がり</span></a>
      <a class="tab ${sort === 'new' ? 'on' : ''}" href="${base}?sort=new"><span>新しい話題</span></a>
    </div>
    ${topics.map(topicRow).join('') || '<p class="empty">まだ話題がありません</p>'}`;
}

export function searchBody(r: SearchResult): string {
  return `
    ${r.tags.length ? `<div class="result-head">タグ</div>
      <div class="tag-chips">${r.tags.map(x => `<a class="tag-chip" href="/tag/${encodeURIComponent(x.tag)}">#${esc(x.tag)}<span>${x.n}</span></a>`).join('')}</div>` : ''}
    <div class="result-head">話題 ${r.topics.length}件</div>
    ${r.topics.map(topicRow).join('') || '<p class="empty">見つかりませんでした</p>'}
    <div class="result-head">ひとこと ${r.posts.length}件</div>
    ${r.posts.map(p => `
      <a class="post-hit" href="/t/${p.topicId}">
        <div class="sub">${esc(p.topicTitle)}・立場：${esc(p.stance)}</div>
        <div>${esc(p.text)}</div>
      </a>`).join('') || '<p class="empty">見つかりませんでした</p>'}`;
}

// ---------- 話題を立てる ----------
export function newPage(usedTags: string[], canCreate: boolean, dailyLimit: number, ui: Ui, siteKey: string): string {
  if (!canCreate) {
    return single(`${backHeader('話題を立てる')}
      <div class="notice-plain">話題を立てられるのは1日に ${dailyLimit} 件までです。時間をおいて、もう一度お試しください。</div>`);
  }
  const tags = sortTags([...new Set([...PRESET_TAGS.map(([t]) => t), ...usedTags])]);
  return single(`
    ${backHeader('話題を立てる')}
    <form class="new-form" autocomplete="off" onsubmit="return false">
      <label>問い</label>
      <input type="text" id="nt-title" maxlength="${TITLE_MAX}" placeholder="例：学校の制服は廃止すべきか">
      <div id="nt-similar"></div>

      <label>立場（任意）</label>
      <div class="hint">空欄なら「そう思う」「そう思わない」になります。</div>
      <div class="two">
        <input type="text" id="nt-a" maxlength="${CAMP_MAX}" placeholder="例：廃止すべき">
        <input type="text" id="nt-b" maxlength="${CAMP_MAX}" placeholder="例：残すべき">
      </div>

      <!-- 最初は問いと立場だけ。ほかはたたむ（X に書くのと同じくらいの手間で立てられるように） -->
      <details class="more-settings">
        <summary>詳しく設定する</summary>
        <label>最初のひとこと</label>
        <textarea id="nt-first" rows="2" placeholder="この問いについてのあなたの意見"></textarea>
        <div class="stance-chips" id="nt-first-stance"></div>

        <label>タグ（5つまで）</label>
        <div class="tag-chips pick-chips">${tags.map(t => `<button type="button" class="tag-chip ${ui.newTags.includes(t) ? 'on' : ''}" data-tagpick="${esc(t)}">#${esc(t)}</button>`).join('')}</div>
        <input type="text" id="nt-tags" placeholder="その他のタグ（スペース区切り）">

        <label>きっかけになった X のポスト</label>
        <input type="url" id="nt-origin" inputmode="url" placeholder="https://x.com/…/status/…">

        <label>中間の立場</label>
        <input type="text" id="nt-mid" maxlength="${CAMP_MAX}" placeholder="例：選べるようにすべき">

        <label>前提となる事実</label>
        <div class="hint">事実だけを、1行に1つ。</div>
        <textarea id="nt-premise" rows="4" placeholder="例：制服一式は、数万円かかることが多い&#10;例：一部の自治体では、制服を選択制にした学校がある"></textarea>

        <label>印</label>
        <select id="nt-sens"><option value="0">なし</option><option value="1">センシティブ（書き込みを伏せる）</option><option value="2">成人向け（一覧に出さない）</option></select>
      </details>

      ${turnstileBox(siteKey)}
      <div class="row">
        <a class="ghost-link" href="/">キャンセル</a>
        <button type="button" class="primary" data-create data-needs-check disabled>話題を立てる</button>
      </div>
    </form>`);
}

export function firstStanceChips(names: [string, string][], chosen: string): string {
  return '<span class="muted small">立場</span>' + names.map(([id, name]) =>
    `<button type="button" class="stance-chip ${chosen === id ? 'on' : ''}" data-first="${id}">${esc(name)}</button>`).join('');
}

export function similarBox(list: { id: string; title: string }[]): string {
  return list.length ? `
    <div class="similar">
      <div class="similar-head">似た話題がすでにあります。同じ内容であれば、こちらに参加してください。</div>
      ${list.map(t => `<a href="/t/${t.id}">${esc(t.title)}</a>`).join('')}
    </div>` : '';
}

// ロボット対策の確認欄（Cloudflare Turnstile）。画面側で中身を入れる
export function turnstileBox(siteKey: string): string {
  return `<div class="turnstile" data-sitekey="${esc(siteKey)}"><div class="turnstile-status">確認しています…</div></div>`;
}

// ---------- 話題の画面 ----------
function campOf(t: TopicView, id: string): Camp | undefined { return t.camps.find(c => c.id === id); }
function campShort(t: TopicView, id: string): string {
  const c = campOf(t, id);
  return c ? (c.short || cut(c.name, 10)) : '';
}
// 立場の並び順は見る人ごとに変える。先頭の立場が主役に見えないように
function campOrder(t: TopicView, v: Viewer): Camp[] { return seededOrder(t.camps, v.seed, c => c.id); }
export function stanceLabel(t: TopicView, p: Post): string {
  if (p.campId !== NONE) return campShort(t, p.campId);
  return p.kind === 'info' ? '補足' : p.kind === 'a' ? '回答' : '質問';
}

export function topicPage(t: TopicView, v: Viewer, ui: Ui, ads = false): string {
  // 印の付いた話題：成人向けは（設定でオンにしていなければ）題名ごと、センシティブは書き込みを伏せて「表示する」で見せる
  const shown = !t.sensitivity || ui.revealed.has(t.id);
  if (!shown && t.sensitivity === 2 && !v.adult) {
    return single(`
      <div class="tl-header"><a href="/" class="back-btn" aria-label="戻る">←</a><div class="tl-title">成人向けの話題</div></div>
      <div class="sens-cover"><div>成人向けの内容を含む話題です。</div><button class="small" data-sens-reveal>表示する</button></div>`);
  }
  const cover = '<div class="sens-cover"><div>センシティブな内容を含む話題です。</div><button class="small" data-sens-reveal>表示する</button></div>';
  return `
    <div class="x-layout">
      <div class="timeline">
        <div class="tl-header">
          <a href="/" class="back-btn" aria-label="戻る">←</a>
          <div class="topic-head">
            <div class="tl-title">${esc(t.title)}${sensTag(t.sensitivity)}</div>
            ${premiseLine(t, ui)}
            ${t.tags.length ? `<div class="tag-line">${t.tags.map(tag => `<a class="tag-text" href="/tag/${encodeURIComponent(tag)}">#${esc(tag)}</a>`).join('')}</div>` : ''}
          </div>
          <span class="spacer"></span>
          <div class="head-icons">
            <button class="icon-btn" data-share aria-label="共有" title="共有">${SHARE_ICON}</button>
            <button class="icon-btn" data-topic-menu aria-label="その他の操作" title="その他の操作">${MORE_ICON}</button>
          </div>
        </div>
        ${t.modStatus === 'pending' && (t.mine || t.myCamp) ? '<div class="notice topic-mod-note">この話題は確認中のため、一時的にトップや検索に表示されていません。議論はそのまま続けられます。</div>' : ''}
        ${debateStrip(t)}
        <div class="tabs-bar">${tabs(t, v, ui)}${ui.filter === 'info' ? '' : orderPick(ui)}</div>
        ${!shown ? cover : ui.filter === 'info' ? `<div class="info-inline">${sideCards(t)}</div>`
          : `<div class="compose-inline">${composer(t, v, ui)}</div><div class="feed">${feed(t, v, ui)}</div>
             <button class="fab phone-only" data-compose aria-label="ひとことを書く">書く</button>`}
      </div>
      <aside class="sidebar">${sideCards(t)}${adSlot(ads, 'side')}${sideLinks()}</aside>
    </div>`;
}

function tabs(t: TopicView, v: Viewer, ui: Ui): string {
  const tab = (id: string, label: string, extra = '') =>
    `<button class="tab ${extra} ${ui.filter === id ? 'on' : ''}" data-filter="${id}"><span>${esc(label)}</span></button>`;
  return `
    <div class="tabs">
      ${tab('all', 'すべて')}
      ${campOrder(t, v).map(c => tab(c.id, campShort(t, c.id))).join('')}
      ${(() => {
        const open = t.posts.filter(p => !p.replyTo && p.campId === NONE && p.kind === 'q' && !p.deleted && !t.posts.some(r => r.replyTo === p.id && !r.deleted)).length;
        return `<button class="tab ${ui.filter === NONE ? 'on' : ''}" data-filter="${NONE}"><span>質問・補足</span>${open ? `<span class="tab-count" title="答えのない質問">${open}</span>` : ''}</button>`;
      })()}
    </div>`;
}

// 題のすぐ下の「前提 N件・争いあり M ›」。押すと前提が開く（パソコンでも、タイムラインの位置に前提を出す）
function premiseLine(t: TopicView, ui: Ui): string {
  const disputed = t.premise.filter(f => f.objections >= DISPUTE_THRESHOLD).length;
  return ui.filter === 'info'
    ? `<button class="premise-line" data-filter="all">‹ タイムラインに戻る</button>`
    : `<button class="premise-line" data-filter="info">前提 ${t.premise.length}件・<span class="${disputed ? 'dispute' : ''}">争いあり ${disputed}</span> ›</button>`;
}

function sideCards(t: TopicView): string {
  return `
    <section class="card"><h2>前提</h2>${premise(t)}</section>
    ${debateCard(t)}
    ${t.proposals.length ? `<section class="card"><h2>提案中の立場</h2>${proposals(t)}</section>` : ''}`;
}

function premise(t: TopicView): string {
  const origin = t.originUrl
    ? `<div class="card-row origin-row"><span class="muted small">きっかけ</span><a href="${esc(t.originUrl)}" target="_blank" rel="nofollow noopener noreferrer">X のポスト ↗</a></div>` : '';
  const foot = `
    <div class="card-row premise-foot">
      <button class="link" data-fact-add>前提を追加</button>
      ${t.editLogCount ? '<button class="link muted-link" data-edit-log>編集履歴</button>' : ''}
    </div>`;
  if (!t.premise.length) return `${origin}<div class="card-row muted small">まだありません。「補足」の投稿から追加することもできます</div>${foot}`;
  return origin + t.premise.map(f => {
    const disputed = f.objections >= DISPUTE_THRESHOLD;
    return `
      <div class="card-row fact">
        <div>${disputed ? '<span class="dispute">争いあり</span> ' : ''}${esc(f.text)}</div>
        ${sourcesHtml(f.sources)}
        <div class="card-meta reveal">
          ${f.byCamp.length ? `<span>異議：${f.byCamp.map(x => `${esc(campShort(t, x.campId))} ${x.n}`).join('、')}</span>` : ''}
          <button class="link" data-fact-edit="${f.id}">編集</button>
          ${disputed ? `<button class="link" data-fact-del="${f.id}">削除</button>` : ''}
          <button class="link ${f.objected ? 'on' : ''}" data-object="${f.id}">${f.objected ? '異議を取り消す' : '異議あり'}</button>
        </div>
      </div>`;
  }).join('') + foot;
}

function proposals(t: TopicView): string {
  return `
    ${t.proposals.map(pr => `
      <div class="card-row proposal">
        <div>
          <div class="claim">${esc(pr.name)}</div>
          ${pr.reason ? `<div class="sub">${esc(pr.reason)}</div>` : ''}
        </div>
        <button class="link ${pr.supported ? 'done' : ''}" data-support="${pr.id}" title="${pr.supported ? 'もう一度押すと取り消せます' : ''}">
          ${pr.supported ? '参加予定' : 'この立場で参加する'} ${pr.supporters}/${PROPOSAL_THRESHOLD}人
        </button>
      </div>`).join('')}`;
}

// 投稿欄。立場ボタンを選んで送る。ログインしていない人には、ログインへの案内を出す
export function composer(t: TopicView, v: Viewer, ui: Ui): string {
  // ログインしていない人も書ける。投稿を押すと、書いた内容を覚えたまま登録へ進み、登録のあとで欄に戻す
  if (!ui.stance) ui.stance = t.myCamp || (t.camps.some(c => c.id === ui.filter) ? ui.filter : '');
  const chip = (value: string, label: string) =>
    `<button type="button" class="stance-chip ${ui.stance === value ? 'on' : ''}" data-stance="${value}">${esc(label)}</button>`;
  return `
    <div class="compose">
      <div class="compose-main">
        <textarea rows="2" placeholder="この話題についてのひとこと"></textarea>
        ${sourceInputs()}
        <div class="compose-foot">
          <div class="stance-chips">
            <span class="muted small">立場</span>
            ${campOrder(t, v).map(c => chip(c.id, campShort(t, c.id))).join('')}
            ${chip(`${NONE}:q`, '質問')}${chip(`${NONE}:info`, '補足')}
            <button type="button" class="stance-chip stance-new" data-propose>＋ 新しい立場</button>
            <button class="primary" data-post>${v.loggedIn || v.guestOK ? '送信' : '登録して投稿'}</button>
          </div>
          ${v.loggedIn ? '' : v.guestOK ? '<div class="muted small guest-note">書き込むと<a href="/terms" target="_blank">利用規約</a>に同意したことになります。今は登録しなくても書き込めます（<a href="/guide#account">登録について</a>）</div>' : '<div class="muted small guest-note">投稿には登録（無料）が必要です。書いた内容は登録後に戻ります。<a href="/login" data-login>ログイン</a></div>'}
        </div>
      </div>
    </div>`;
}

function orderPick(ui: Ui): string {
  const o = (id: string, label: string) => `<option value="${id}" ${ui.order === id ? 'selected' : ''}>${label}</option>`;
  return `<select class="order-pick" data-order-select aria-label="並び替え">${o('rec', 'おすすめ')}${o('new', '新しい順')}${o('like', 'それな順')}</select>`;
}
function orderRow(ui: Ui): string {
  const b = (id: string, label: string) => `<button class="order-btn ${ui.order === id ? 'on' : ''}" data-order="${id}">${label}</button>`;
  return `<div class="order-row"><span class="muted small">並び替え</span>${b('rec', 'おすすめ')}${b('new', '新しい順')}${b('like', 'それな順')}</div>`;
}

const isPending = (p: Post) => p.views < NEW_VIEW_TARGET;
// 「おすすめ」の並び：同じ立場の「それな」に、反対の立場からの「なるほど」を2倍の重みで足す（反対側も認めた意見を上に）
const rate = (p: Post) => score(p.likes + p.naruhodo * NARUHODO_WEIGHT, p.views);

// 1つの立場の中の並び。評価済み（それなの率順）と新着を1件ずつ交互に
function laneList(posts: Post[], v: Viewer, laneId: string): { list: Post[]; fresh: Set<string> } {
  // 評価待ちに入れるのは、1人につき一番古い1件だけ（匿名 ID で同じ人を見分ける）
  const pending = posts.filter(isPending).sort((a, b) => a.createdAt - b.createdAt);
  const queued: Post[] = [], seen = new Set<string>();
  for (const p of pending) if (!seen.has(p.anon)) { seen.add(p.anon); queued.push(p); }
  // 新着枠に出すものは見る人ごとに変える（同じものばかりが評価されないように）
  const fresh = seededOrder(queued, v.seed + laneId, p => p.id).slice(0, NEW_LIMIT);
  const rated = posts.filter(p => !isPending(p)).sort((a, b) => rate(b) - rate(a));
  const list: Post[] = [];
  for (let i = 0; i < Math.max(rated.length, fresh.length); i++) {
    if (rated[i]) list.push(rated[i]);
    if (fresh[i]) list.push(fresh[i]);
  }
  return { list, fresh: new Set(fresh.map(p => p.id)) };
}

// タイムライン。各立場の上位から1件ずつ順番に並べるので、人数の多い立場が埋め尽くさない
// 質問・補足の並び：まだ答えのない質問（「私も知りたい」が多い順）→ 補足（新しい順）→ 答えのある質問（新しい順）
function questionList(t: TopicView): Post[] {
  const tops = t.posts.filter(p => !p.replyTo && p.campId === NONE);
  const answered = (p: Post) => t.posts.some(r => r.replyTo === p.id && !r.deleted);
  const open = tops.filter(p => p.kind === 'q' && !answered(p)).sort((a, b) => b.likes - a.likes || b.createdAt - a.createdAt);
  const info = tops.filter(p => p.kind === 'info').sort((a, b) => b.createdAt - a.createdAt);
  const done = tops.filter(p => p.kind === 'q' && answered(p)).sort((a, b) => b.createdAt - a.createdAt);
  return [...open, ...info, ...done];
}

// タイムライン。各立場の上位から1件ずつ順番に並べるので、人数の多い立場が埋め尽くさない。
// 質問・補足は「すべて」には混ぜず、答えのない質問があることだけを上に知らせる
export function feed(t: TopicView, v: Viewer, ui: Ui): string {
  const empty = '<p class="empty">最初の「ひとこと」を書いてみませんか</p>';
  if (ui.filter === NONE) {
    const list = questionList(t);
    return list.length ? list.map(p => thread(t, p, v, ui, false)).join('') : '<p class="empty">まだ「質問」「補足」はありません</p>';
  }
  const unanswered = t.posts.filter(p => !p.replyTo && p.campId === NONE && p.kind === 'q' && !p.deleted
    && !t.posts.some(r => r.replyTo === p.id && !r.deleted)).length;
  const strip = '';   // 答えのない質問の数は「質問・補足」のタブに出す
  const tops = t.posts.filter(p => !p.replyTo && p.campId !== NONE);
  if (ui.order === 'new' || ui.order === 'like') {
    const list = tops.filter(p => ui.filter === 'all' || p.campId === ui.filter)
      .sort(ui.order === 'new'
        ? (a, b) => b.createdAt - a.createdAt
        : (a, b) => b.likes - a.likes || b.createdAt - a.createdAt);
    return strip + (list.length ? list.map(p => thread(t, p, v, ui, false)).join('') : empty);
  }
  const lanes = ui.filter === 'all' ? campOrder(t, v).map(c => c.id) : [ui.filter];
  const top = tops.filter(p => lanes.includes(p.campId));
  // 自分の評価待ちのひとことは一番上に（送ったものが見えないと不安になるため）
  const myPending = top.filter(p => p.mine && isPending(p)).sort((a, b) => b.createdAt - a.createdAt);
  const fresh = new Set<string>();
  const lists = lanes.map(l => {
    const r = laneList(top.filter(p => p.campId === l && !myPending.includes(p)), v, l);
    r.fresh.forEach(id => fresh.add(id));
    return r.list;
  });
  const merged: Post[] = [];
  for (let i = 0; lists.some(L => i < L.length); i++) for (const L of lists) if (L[i]) merged.push(L[i]);
  const all = [...myPending, ...merged];
  return strip + (all.length ? all.map(p => thread(t, p, v, ui, fresh.has(p.id))).join('') : empty);
}

// ひとことと、それへの反論をひとまとまりにする
// 議論場の札と状態の名前
export const KIND_ARENA = '<span class="kind-tag">1対1</span>';
const ARENA_LABEL: Record<ArenaStatus, string> = { open: '議論中', timeout: '時間切れ', conceded: '納得', deadlock: '平行線', ended: '終了' };

// 「反論」から続く往復（反論を含む）
function chainOf(t: TopicView, first: Post): Post[] {
  const out = [first];
  for (;;) {
    const next = t.posts.find(p => p.replyTo === out[out.length - 1].id && !p.deleted);
    if (!next) return out;
    out.push(next);
  }
}

// ひとことと、それへの反論・回答をひとまとまりにする。反論からの往復は、議論場に移るまでタイムラインで続く
function thread(t: TopicView, p: Post, v: Viewer, ui: Ui, isNew: boolean): string {
  const replies = t.posts.filter(r => r.replyTo === p.id).sort((a, b) => rate(b) - rate(a));
  const parts = replies.map(r => {
    if (p.campId === NONE) return postHtml(t, r, v, ui, { isReply: true });
    const arena = t.arenas.find(a => a.firstId === r.id);
    if (arena) {
      return `${postHtml(t, r, v, ui, { isReply: true })}
        <a class="arena-line" href="/t/${t.id}/a/${arena.id}">議論場で続いています（${ARENA_LABEL[arena.status]}${arena.recruiting ? '・交代を募集中' : ''}${arena.comments ? `・コメント ${arena.comments}` : ''}）›</a>`;
    }
    // 往復の最後の書き込みにだけ、次に書く人の「返す」を付ける
    const chain = chainOf(t, r);
    const last = chain[chain.length - 1];
    const isPart = p.mine || r.mine;
    const action = !isPart ? ''
      : last.mine ? '<span class="muted small">相手の返信を待っています</span>'
      : `<button class="act" data-chain="${last.id}">返信する</button>`;
    return chain.map((c, i) => postHtml(t, c, v, ui, { isReply: true, chainAction: i === chain.length - 1 ? action : '' })).join('');
  });
  return `<div class="thread">
    ${postHtml(t, p, v, ui, { isNew, hasThread: replies.length > 0 })}
    ${parts.join('')}
  </div>`;
}

// タイムラインで「返す」の小窓
export function chainBox(t: TopicView, last: Post): string {
  return `
    <div class="box">
      <div class="reply-src">
        <div class="xp-head"><span class="xp-name">${esc(last.anon)}</span><span class="xp-time">立場：${esc(stanceLabel(t, last))}</span></div>
        <div>${esc(last.text)}</div>
      </div>
      <textarea id="reply-text" rows="3" placeholder="返信を書く"></textarea>
      ${sourceInputs()}
      <p class="hint small">やり取りが続くと、2人だけの「議論場」に移ります。</p>
      <div class="compose-foot">
        <span class="spacer"></span>
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary" data-ok>返信する</button>
      </div>
    </div>`;
}

// ---------- 議論場 ----------
function arenaCamp(a: ArenaView, campId: string): string {
  const c = a.camps.find(x => x.id === campId);
  return c ? c.name : '';
}
function arenaStatusText(a: ArenaView, now = Date.now()): string {
  const next = a.players[a.nextSlot].anon;
  switch (a.status) {
    case 'conceded': return `<span class="st settled">納得</span>${esc(a.loser)} が相手の意見に納得して終わりました`;
    case 'deadlock': return '<span class="st deadlock">平行線</span>2人とも平行線として終えることに同意しました';
    case 'ended': return `<span class="st deadlock">終了</span>${esc(a.endedFor)} の時間切れで終了しました（勝ち負けは付けません）`;
    case 'timeout': return `<span class="st timeout">時間切れ</span>${esc(next)} の返信が${a.awayKind === 'away' ? '延長した期限を過ぎても' : '24時間'}ありません`;
  }
  if (a.awayKind === 'away') return `<span class="st open">延長中</span>${esc(next)} の申請で、持ち時間を延長しています（あと${remain(a.deadline - now)}）`;
  return `<span class="st open">議論中</span>${esc(next)} の番です（あと${remain(a.deadline - now)}）`;
}

// 「待ってほしい」の申し出の欄（自分の番のとき）
// 持ち時間の延長の申請（小窓）。ボタンの並びを崩さないよう、その場で開かずに小窓に出す
export function waitAskBox(first: boolean): string {
  return `
    <div class="box">
      <h3>持ち時間の延長を申請</h3>
      <div class="wait-form">
        <select id="wait-hours">${WAIT_HOURS.map(h => `<option value="${h}">${waitLabel(h)}</option>`).join('')}</select>
        <input type="text" id="wait-reason" maxlength="100" placeholder="理由（任意）　例：仕事で夜まで見られません">
        <div class="hint">${first ? '1回目なので、すぐに延長されます。' : '相手の承認か、観客の投票で決まります。'}</div>
      </div>
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary small" data-ok>申請する</button>
      </div>
    </div>`;
}

// 申し出の記録。いまの番の申し出には、相手の返事と観客の判断のボタンを付ける
function waitLine(w: ArenaWait): string {
  const tally = `賛成 ${w.yes}・反対 ${w.no}`;
  const result = w.status === 'granted'
    ? (w.grantedBy === 'auto' ? '1回目のため延長されました' : w.grantedBy === 'opponent' ? '相手が承認し、延長されました' : `観客の投票で延長されました（${tally}）`)
    : w.status === 'rejected' ? `延長されませんでした（${tally}）`
    : w.status === 'refused' ? `相手は承認しませんでした。期限までの観客の投票で決まります（${tally}）`
    : `相手の返事を待っています。返事がない場合は観客の投票で決まります（${tally}）`;
  const answer = w.canAnswer ? `<button class="link" data-wait-answer="${w.id}" data-yes="1">承認する</button><button class="link" data-wait-answer="${w.id}" data-yes="0">承認しない</button>` : '';
  const vote = w.canVote ? `
    <button class="act like ${w.myVote === true ? 'on' : ''}" data-wait-vote="${w.id}" data-yes="1">延長に賛成 ${w.yes}</button>
    <button class="act like ${w.myVote === false ? 'on' : ''}" data-wait-vote="${w.id}" data-yes="0">延長に反対 ${w.no}</button>` : '';
  return `
    <div class="wait-line">
      <div>${esc(w.anon)}${w.mine ? '（あなた）' : ''} が${waitLabel(w.hours)}の延長を申請しました${w.reason ? `：『${esc(w.reason)}』` : ''}</div>
      <div class="muted small">${result}</div>
      ${answer || vote ? `<div class="wait-actions">${answer}${vote}</div>` : ''}
    </div>`;
}
function waitsHtml(a: ArenaView): string {
  // 判断中のもの（相手の返事待ち・観客の判断中）だけ本文に出す。結果の出たものは「…」の経過へ
  const deciding = a.waits.filter(w => w.status === 'pending' || w.status === 'refused');
  return deciding.length ? `<div class="waits">${deciding.map(waitLine).join('')}</div>` : '';
}
// 経過：交代と、結果の出た延長の記録（古い順）
function arenaLog(a: ArenaView): string[] {
  const done = a.waits.filter(w => w.status === 'granted' || w.status === 'rejected');
  const short = (w: ArenaWait) => `${esc(w.anon)}：${waitLabel(w.hours)}の延長 → ${w.status === 'rejected' ? '延長なし'
    : w.grantedBy === 'auto' ? '延長（1回目）' : w.grantedBy === 'opponent' ? '延長（相手が承認）' : '延長（観客の投票）'}${w.reason ? `（${esc(cut(w.reason, 20))}）` : ''}`;
  return [
    ...a.subs.map(s => `交代：${esc(s.from)} → ${esc(s.to)}（${dateLabel(s.at)}）`),
    ...done.map(short),
  ];
}
function arenaMenu(a: ArenaView, live: boolean, me: Slot | null): string {
  const log = arenaLog(a);
  return `
    <details class="arena-menu">
      <summary class="icon-btn" aria-label="表示の切り替えと経過" title="表示の切り替えと経過">${MORE_ICON}</summary>
      <div class="arena-menu-pop">
        <div class="menu-label">発言の並べ方</div>
        <div class="seg menu-seg"><button data-arena-view="list">縦に並べる</button><button data-arena-view="split">左右に分ける</button></div>
        ${live && a.watchers !== undefined ? `
        <div class="menu-label">観戦人数</div>
        <div class="seg menu-seg"><button data-watchers-set="show">表示する</button><button data-watchers-set="hide">表示しない</button></div>` : ''}
        ${!me && live ? `<button class="link watch-btn ${a.watching ? 'done' : ''}" data-arena-act="watch">${a.watching ? '更新の通知を止める' : '更新を通知する'}</button>` : ''}
        ${log.length ? `<div class="menu-label">経過</div><div class="menu-log">${log.map(l => `<div>${l}</div>`).join('')}</div>` : ''}
      </div>
    </details>`;
}

// 交代の募集（時間切れで空いた席も同じ扱い）。誰でも X で広められる（匿名 ID や募集した人は載せない）
export function recruitSlot(a: ArenaView): Slot | null {
  if (a.status !== 'open' && a.status !== 'timeout') return null;
  return a.recruit.A ? 'A' : a.recruit.B ? 'B' : a.status === 'timeout' ? a.nextSlot : null;
}
function recruitBanner(a: ArenaView): string {
  const s = recruitSlot(a);
  if (!s) return '';
  return `
    <div class="notice recruit-banner">
      <div>「${esc(arenaCamp(a, a.players[s].campId))}」側の交代を募集しています</div>
      <button class="link" data-share-recruit="${s}">X で募集を共有</button>
    </div>`;
}

// 終わった議論場のまとめ：終わり方・往復の数・かかった日数・いちばん「なるほど」を集めた発言
function arenaSummary(a: ArenaView): string {
  if (a.status === 'open' || a.status === 'timeout' || !a.msgs.length) return '';
  const first = a.msgs[0].createdAt, last = a.msgs[a.msgs.length - 1].createdAt;
  const days = Math.max(1, Math.ceil((last - first) / DAY));
  const best = [...a.msgs].sort((x, y) => y.naruhodo - x.naruhodo)[0];
  const how = a.status === 'conceded' ? `${esc(a.loser)} が相手の意見に納得して終わりました`
    : a.status === 'deadlock' ? '2人とも納得のうえで、平行線として終わりました'
    : `${esc(a.endedFor)} の時間切れで終わりました`;
  return `
    <div class="arena-summary">
      <div class="arena-summary-head">${how}</div>
      <div class="arena-summary-nums"><span><b>${a.msgs.length}</b> 回のやり取り</span><span><b>${days}</b> 日間</span>${a.subs.length ? `<span>交代 <b>${a.subs.length}</b> 回</span>` : ''}</div>
      ${best && best.naruhodo > 0 ? `
        <div class="arena-best">
          <div class="muted small">いちばん「なるほど」を集めた発言（${best.naruhodo}）</div>
          <div class="arena-best-text">${esc(cut(best.text, 140))}</div>
          <div class="muted small">${esc(best.anon)} · ${esc(arenaCamp(a, best.campId))}</div>
        </div>` : ''}
    </div>`;
}

// 議論場の発言の本文。「＞」で始まる行は、相手の発言からの引用として出す
function turnText(text: string): string {
  const lines = text.split('\n');
  return lines.map((l, i) => /^[>＞]/.test(l)
    ? `<span class="quote">${esc(l.replace(/^[>＞]\s?/, ''))}</span>`
    : esc(l) + (i < lines.length - 1 ? '\n' : '')).join('');
}

export function arenaPage(a: ArenaView, v: Viewer, ui?: Ui): string {
  // 印の付いた話題の議論場：話題のページと同じく伏せる（リンクから直接来た人向け。話題のページで「表示する」を押していれば伏せない）
  if (a.topicSensitivity && !ui?.revealed.has(a.topicId)) {
    const adult = a.topicSensitivity === 2 && !v.adult;
    return single(`
      <div class="tl-header"><a href="${adult ? '/' : `/t/${a.topicId}`}" class="back-btn" aria-label="戻る">←</a>
        <div><div class="tl-title">議論場${KIND_ARENA}</div>${adult ? '' : `<div class="tl-sub"><a href="/t/${a.topicId}">${esc(a.topicTitle)}</a></div>`}</div></div>
      <div class="sens-cover"><div>${adult ? '成人向けの' : 'センシティブな'}内容を含む話題です。</div><button class="small" data-sens-reveal>表示する</button></div>`);
  }
  const live = a.status === 'open' || a.status === 'timeout';
  const me = a.mySlot;
  const myTurn = live && me === a.nextSlot;
  const name = (s: Slot) => esc(arenaCamp(a, a.players[s].campId));

  // 発言の下は立場を主に出す。匿名 ID は、交代があって同じ立場に2人以上いるときだけ（見分けるため）
  const turns = a.msgs.map((m, i) => {
    const isLast = i === a.msgs.length - 1;
    const canAdd = isLast && m.mine && !m.addendum && live;
    // 左右に分ける表示のとき、上の2人の欄と同じ側に寄せる（交代しても立場は同じ）
    const slot: Slot = m.campId === a.players.A.campId ? 'A' : 'B';
    return `
      <li class="turn side-${slot}" data-pid="${m.id}">
        <div class="turn-body">${turnText(m.text)}</div>
        ${m.addendum ? `<div class="addendum"><span class="addendum-label">追記</span>${esc(m.addendum)}</div>` : ''}
        ${sourcesHtml(m.sources)}
        <div class="turn-foot">
          <span class="xp-meta">${esc(arenaCamp(a, m.campId))}${a.subs.length ? ` · ${esc(m.anon)}` : ''}${m.mine ? '（あなた）' : ''} · ${ago(m.createdAt)}</span>
          <span class="spacer"></span>
          ${me ? `<span class="act static">なるほど ${m.naruhodo}</span>`
            : `<button class="act like ${m.naruhodoed ? 'on' : ''}" data-naruhodo="${m.id}">なるほど ${m.naruhodo}</button>`}
          ${canAdd ? '<button class="link" data-addendum>追記する</button>' : ''}
        </div>
      </li>`;
  }).join('');

  const candBox = a.candidates.length ? `
    <div class="notice">${a.candidates.map(c => `<div>${esc(c)} が、あなたとの交代を希望しています
      <button class="link" data-accept="${esc(c)}">交代する</button></div>`).join('')}</div>` : '';

  const other = me ? (me === 'A' ? 'B' : 'A') : null;
  // 相手から平行線の提案が来ているときは、たたまずに見えるところに出す
  const deadlockNote = me && other && a.deadlock[other] && !a.deadlock[me]
    ? '<div class="notice">相手が平行線として終えることを提案しています <button class="link" data-arena-act="deadlock">同意する</button></div>' : '';
  // 議論を終える・時間を延ばす・交代する。たたまずに並べ、押したときの確認で何が起きるかを説明する
  const moreBox = (withConcede: boolean, withWait: boolean) => me ? `
    <div class="arena-more">
      ${withConcede ? '<button class="link" data-arena-act="concede">相手の意見に納得して終える</button>' : ''}
      <button class="link ${a.deadlock[me] ? 'done' : ''}" data-arena-act="deadlock">${a.deadlock[me] ? '平行線の提案を取り消す' : other && a.deadlock[other] ? '平行線の提案に同意する' : '平行線として終える'}</button>
      ${withWait ? '<button class="link" data-arena-act="wait-open">時間延長を申請</button>' : ''}
      ${a.recruit[me] ? '<span class="muted small">交代を募集中です</span>' : '<button class="link" data-arena-act="recruit">交代を募集する</button>'}
    </div>` : '';
  let foot = '';

  if (live && myTurn) {
    foot = `
      ${deadlockNote}
      <div class="turn-input arena-input">
        <div class="quote-row"><button class="link" data-quote-start>相手の発言の一部を引用する</button></div>
        <textarea rows="3" placeholder="返信を書く"></textarea>
        ${sourceInputs()}
        <div class="turn-actions">
          <span class="spacer"></span>
          <button class="primary small" data-arena-act="post">返信する</button>
        </div>
        ${moreBox(true, a.canAskWait)}
      </div>`;
  } else if (live) {
    const next = esc(a.players[a.nextSlot].anon);
    const timeoutChoice = a.status === 'timeout' && me && !a.waited
      ? '<button class="link" data-arena-act="wait">引き続き待つ</button><button class="link" data-arena-act="end">ここで終了する</button>' : '';
    foot = `
      ${deadlockNote}
      <div class="turn-actions waiting arena-input">
        <span class="muted small">${a.status === 'timeout' ? `${next} の返信がありません` : `${next} の返信を待っています`}</span>
        ${timeoutChoice}
      </div>
      ${me ? `<div class="arena-input">${moreBox(false, false)}</div>` : ''}`;
  }

  const main = `
    <div class="tl-header">
      <a href="/t/${a.topicId}" class="back-btn" aria-label="戻る">←</a>
      <div><div class="tl-title">議論場${KIND_ARENA}<a class="help-link" href="/guide#arena" aria-label="議論場とは">？</a></div>
        <div class="tl-sub"><a href="/t/${a.topicId}">${esc(a.topicTitle)}</a></div></div>
      <span class="spacer"></span>
      <div class="head-icons">${arenaMenu(a, live, me)}</div>
    </div>
    <div class="teams arena-players">
      ${(['A', 'B'] as Slot[]).map(s => {
        const p = a.players[s];
        return `
        <div class="team player-${s}">
          <div class="player-name">${live ? `<span class="presence-dot ${p.here ? 'here' : ''}" title="${p.here ? '議論場を見ています' : p.seenAt ? `${ago(p.seenAt)}に見ました` : 'まだ見ていません'}"></span>` : ''}${esc(p.anon)}${p.me ? '（あなた）' : ''}</div>
          <div class="muted small">${name(s)}</div>
          ${live ? `
            ${p.note ? `<div class="player-note">${esc(p.note)}</div>` : ''}
            ${p.me ? `<button class="link small" data-arena-note>${p.note ? '状態を変える' : '状態を出す'}</button>` : ''}` : ''}
        </div>`;
      }).join('<div class="team-vs">と</div>')}
    </div>
    ${a.msgs.length >= 2 ? `
    <details class="arena-issue">
      <summary><span class="arena-issue-head">論点</span><span class="arena-issue-sum">${esc(cut(a.msgs[0].text, 40))}</span></summary>
      ${a.msgs.slice(0, 2).map(m => `<div class="arena-issue-line"><span class="muted small">${esc(m.anon)}</span>${esc(cut(m.text, 200))}</div>`).join('')}
    </details>` : ''}
    ${arenaSummary(a) || `
    <div class="issue-status arena-status">${arenaStatusText(a)}
      ${live && a.watchers !== undefined ? `<span class="watchers">${a.watchers} 人が観戦中</span>` : ''}</div>`}
    ${waitsHtml(a)}
    ${recruitBanner(a)}
    ${candBox}
    <ol class="turns arena-turns">${turns}</ol>
    ${foot}`;

  // 観客席：時間切れ・交代の募集のときの「代わりに入る」、立候補
  const takeover: string[] = [];
  if (live && !me && (v.loggedIn || v.guestOK)) {
    for (const s of ['A', 'B'] as Slot[]) {
      const p = a.players[s];
      const sameCamp = a.myCamp === p.campId;
      const timedOut = a.status === 'timeout' && a.nextSlot === s;
      if (timedOut || a.recruit[s]) {
        takeover.push(`<div class="notice">${timedOut ? `${esc(p.anon)} の返信が時間切れです` : `${esc(p.anon)}（${name(s)}）が交代を募集しています`}
          ${sameCamp ? `<button class="link" data-take="${s}">交代して参加する</button>` : `<span class="muted small">交代できるのは「${name(s)}」の立場の人だけです</span>`}</div>`);
      } else if (sameCamp) {
        takeover.push(`<div class="notice-plain">${a.applied[s] ? `<span class="muted small">${esc(p.anon)} との交代に立候補しています</span>`
          : `<button class="link" data-volunteer="${s}">${esc(p.anon)} との交代に立候補する</button>`}</div>`);
      }
    }
  }
  const seats = `
    <div class="seats">
      <h2>観客席</h2>
      ${takeover.join('')}
      <div class="seat-list">
        ${a.comments.length ? a.comments.map(c => `
          <div class="seat"><div>${esc(c.text)}</div>
            <div class="seat-foot"><span class="xp-meta">${c.campId ? `${esc(arenaCamp(a, c.campId))} · ` : ''}${esc(c.anon)}${c.mine ? '（あなた）' : ''} · ${ago(c.at)}</span></div></div>`).join('')
          : '<div class="muted small">まだコメントはありません</div>'}
      </div>
      ${me ? '<div class="muted small seat-note">議論中の2人は、観客席に書き込めません</div>' : `
        <div class="seat-input">
          <textarea rows="2" placeholder="コメントを書く"></textarea>
          <button class="small" data-comment>コメント</button>
        </div>`}
    </div>`;

  return `
    <div class="arena-layout">
      <div class="timeline" style="view-transition-name: arena">${main}</div>
      <aside class="arena-side">${seats}</aside>
    </div>`;
}

// ---------- 討論場 ----------
export const KIND_DEBATE = '<span class="kind-tag">チーム戦</span>';
function debateStrip(t: TopicView): string {
  const list = [...t.debates.filter(d => d.status !== 'done'), ...t.debates.filter(d => d.status === 'done')].slice(0, 2);
  if (!list.length) return '';
  return `<div class="debate-strip">${list.map(d => `
    <a class="debate-card arena-link" href="/t/${t.id}/d/${d.id}">
      <span class="debate-card-label">討論場${KIND_DEBATE} 第${d.no}回</span>
      <span class="debate-card-main">${esc(d.campA)} と ${esc(d.campB)}</span>
      <span class="debate-card-status">${esc(d.label)} ›</span>
    </a>`).join('')}</div>`;
}
function debateCard(t: TopicView): string {
  return `
    <section class="card"><h2>討論場${KIND_DEBATE}<a class="help-link" href="/guide#debate" aria-label="討論場とは">？</a></h2>
      ${t.debates.map(d => `
        <a class="card-row arena-link" href="/t/${t.id}/d/${d.id}">
          <div class="claim">第${d.no}回　${esc(d.campA)} と ${esc(d.campB)}</div>
          <div class="sub">${esc(d.label)}</div>
        </a>`).join('')}
      ${t.canOpenDebate ? '<div class="card-row"><button class="link" data-debate-open>討論を開く</button></div>'
        : t.debates.some(d => d.status !== 'done') ? '' : '<div class="card-row muted small">ログインすると、討論を開けます</div>'}
    </section>`;
}
export function debateOpenBox(t: TopicView): string {
  const a = t.camps.find(c => c.pole === 'a') || t.camps[0];
  const b = t.camps.find(c => c.pole === 'b') || t.camps[1];
  const sel = (id: string, chosen: Camp | undefined) => `<select id="${id}">${t.camps.map(c => `<option value="${c.id}" ${c.id === chosen?.id ? 'selected' : ''}>${esc(c.name)}</option>`).join('')}</select>`;
  return `
    <div class="box">
      <h3>討論を開く</h3>
      <p>立場ごとのチームで、主張・反論・まとめを順に出し合います。参加者の名前は表示されます。</p>
      <label class="muted small">対立する2つの立場</label>
      <div class="two-sel">${sel('db-a', a)}<span>と</span>${sel('db-b', b)}</div>
      <label class="muted small" style="display:block;margin-top:10px">各チームの人数</label>
      <select id="db-cap">${[2, 3, 4, 5].map(n => `<option value="${n}" ${n === 3 ? 'selected' : ''}>${n}人</option>`).join('')}</select>
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary small" data-ok>募集を始める</button>
      </div>
    </div>`;
}

function debateDraftBox(d: DebateView, dr: DebateDraft): string {
  const kind = DEBATE_STEPS[dr.step].kind;
  const max = DEBATE_MAX[kind];
  const live = d.status === 'live';
  const last = dr.versions[0];
  return `
    <div class="muted small">${DEBATE_RULE[kind]}</div>
    ${d.pendingQuestions.length ? `<div class="qa answers"><div class="qa-label">相手チームからの質問に回答してください</div>${d.pendingQuestions.map((q, k) => `
      <div class="qa-item"><div class="qa-q">Q. ${esc(q)}</div>
        <textarea class="draft-a" rows="2" maxlength="200" placeholder="回答">${esc(dr.answers[k] || '')}</textarea></div>`).join('')}</div>` : ''}
    <textarea id="debate-draft" rows="6" placeholder="チームの${kind}を書く" data-max="${max}">${esc(dr.text)}</textarea>
    <div class="char-count ${[...dr.text].length > max ? 'over' : ''}"><span id="draft-count">${[...dr.text].length}</span> / ${max}字</div>
    ${kind === '反論' ? `<div class="qa-inputs"><div class="qa-label">相手への質問（任意・2つまで）</div>
      ${[0, 1].map(k => `<input type="text" class="draft-q" maxlength="100" placeholder="質問${k + 1}" value="${esc(dr.questions[k] || '')}">`).join('')}</div>` : ''}
    <div class="turn-actions">
      <span class="muted small">${last ? `最後の保存：${esc(last.handle)}・${ago(last.at)}${dr.versions.length > 1 ? `（${dr.version}回保存）` : ''}` : 'まだ保存されていません'}</span>
      ${dr.versions.length > 1 ? '<button class="link" data-draft-history>履歴</button>' : ''}
      <span class="spacer"></span>
      <button class="small" data-debate-act="draft">下書きを保存</button>
      ${live ? `<button class="small ${dr.approvedByMe ? 'on' : ''}" data-debate-act="approve">提出に同意 ${dr.approvals}/${dr.need}</button>
        <button class="primary small" data-debate-act="submit">提出する</button>` : ''}
    </div>
    ${live ? `<div class="muted small">${dr.urgent ? '残り15分を切ったため、1人でも提出できます。'
      : `チームの過半数（${dr.need}人）が「提出に同意」を押すと提出できます。下書きを変更すると、同意はリセットされます。残り15分を切ると、1人でも提出できます。`}</div>` : ''}`;
}

export function draftHistoryBox(dr: DebateDraft): string {
  return `
    <div class="box">
      <h3>下書きの履歴</h3>
      <div class="edit-log">
        ${dr.versions.map(v => `
          <div class="log-row">
            <div class="muted small">${v.version}回目の保存・${esc(v.handle)}・${ago(v.at)}
              ${v.version < dr.version ? `<button class="link" data-restore="${v.version}">この下書きに戻す</button>` : '（現在の下書き）'}</div>
            <div class="log-to">${esc(v.text) || '<span class="muted">（本文なし）</span>'}</div>
          </div>`).join('')}
      </div>
      <div class="row"><button class="ghost small" data-close>閉じる</button></div>
    </div>`;
}

export function debateWaitBox(d: DebateView): string {
  const side = d.mySide!;
  const bank = d.bank[side];
  const hours = (ms: number) => `${Math.floor(ms / 3600000 * 10) / 10}時間`;
  return `
    <div class="box">
      <h3>時間延長</h3>
      <p>現在の番（${d.current?.kind ?? ''}）の持ち時間を延長します。相手チームの承認は不要です。あなたのチームが延長できる残り時間は ${hours(bank)} です。</p>
      <label class="wait-opt"><input type="radio" name="wait-kind" value="hours" checked> 時間を指定
        <select id="wait-hours">${WAIT_CHOICES.filter(h => h * 3600000 <= bank).map(h => `<option value="${h}">${h}時間延長</option>`).join('')}</select></label>
      <label class="wait-opt"><input type="radio" name="wait-kind" value="until"> 日時を指定して一時停止
        <input type="datetime-local" id="wait-until"></label>
      <input type="text" id="wait-reason" maxlength="60" placeholder="理由（任意）　例：仕事で夜まで書けません">
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary small" data-ok>申請する</button>
      </div>
    </div>`;
}

export function atodashiBox(): string {
  return `
    <div class="box">
      <h3>後出しを指摘する</h3>
      <p>まとめに新しい論点が入っていたら指摘できます。判断は観客がします。</p>
      <input type="text" id="atodashi-note" maxlength="80" placeholder="どこが新しい論点か（任意）">
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary small" data-ok>指摘する</button>
      </div>
    </div>`;
}

export function debatePage(d: DebateView, v: Viewer): string {
  const side = d.mySide;
  const name = (s: Slot) => esc(d.camps[s].name);
  const members = (s: Slot) => `
    <div class="team">
      <div class="team-name">${name(s)}</div>
      <div class="team-members">${d.members[s].map(m => `<span>${esc(m.handle)}${m.me ? '（あなた）' : ''}</span>`).join('')}
        ${Array.from({ length: Math.max(0, d.capacity - d.members[s].length) }, () => '<span class="seat-empty">空き</span>').join('')}</div>
      ${d.status === 'recruit' && !side && d.members[s].length < d.capacity
        ? (d.myCamp && d.myCamp !== d.camps[s].id ? '<span class="muted small">あなたの立場とは異なるチームです</span>' : `<button class="small" data-debate-join="${s}">このチームで参加する</button>`) : ''}
      ${d.status === 'recruit' && side === s ? '<button class="link small" data-debate-act="leave">参加を取り消す</button>' : ''}
    </div>`;

  const statements = d.statements.map(x => `
    <li class="turn statement" data-pid="${x.id}">
      <div class="statement-kind">${x.kind}・${name(x.side)}</div>
      <div class="turn-main">
        ${x.answers.length ? `<div class="qa answers"><div class="qa-label">質問への回答</div>${x.answers.map(a => `
          <div class="qa-item"><div class="qa-q">Q. ${esc(a.q)}</div><div class="qa-a">${a.a ? esc(a.a) : '<span class="muted">（回答なし）</span>'}</div></div>`).join('')}</div>` : ''}
        ${x.late === 'none' ? '<div class="turn-body muted">時間内に提出がありませんでした</div>' : `<div class="turn-body">${esc(x.text)}</div>`}
        ${x.late === 'draft' ? '<div class="muted small">時間切れのため、保存されていた下書きを提出しました</div>' : ''}
        ${x.questions.length ? `<div class="qa questions"><div class="qa-label">「${name(x.side === 'A' ? 'B' : 'A')}」への質問</div>${x.questions.map(q => `<div class="qa-q">Q. ${esc(q)}</div>`).join('')}</div>` : ''}
        ${x.atodashi ? `<div class="atodashi"><span class="atodashi-badge">後出しの指摘</span>「${name(x.atodashi.side)}」の ${esc(x.atodashi.handle)}${x.atodashi.note ? `：${esc(x.atodashi.note)}` : ''}</div>`
          : x.canAtodashi ? `<button class="link small atodashi-btn" data-debate-atodashi="${x.id}">後出しを指摘</button>` : ''}
        <div class="turn-foot">
          <span class="xp-meta">${x.by.map(esc).join('・') || '—'} · ${ago(x.createdAt)}</span>
          <span class="spacer"></span>
          ${side ? `<span class="act static">なるほど ${x.naruhodo}</span>`
            : `<button class="act like ${x.naruhodoed ? 'on' : ''}" data-debate-naruhodo="${x.id}">なるほど ${x.naruhodo}</button>`}
        </div>
      </div>
    </li>`).join('');

  // 今の番の表示。自分のチームの番なら、チームの下書き
  const curWait = d.current ? d.waits.filter(w => w.step === d.current!.step).slice(-1)[0] : null;
  const waitInfo = curWait ? `<div class="wait-note">延長中（${dateLabel(d.current!.deadline)}まで）・${esc(curWait.handle)}${curWait.reason ? `『${esc(curWait.reason)}』` : ''}</div>` : '';
  const hours = (ms: number) => `${Math.floor(ms / 3600000 * 10) / 10}時間`;
  const waitBtn = side && d.status === 'live' ? `<button class="link" data-debate-wait>時間延長</button><span class="muted small">延長できる残り ${hours(d.bank[side])}</span>` : '';
  let now = '';
  if (d.status === 'recruit') {
    now = `<div class="debate-now" id="debate-now">
      <div>各チーム${d.capacity}人がそろうと始まります。主張の持ち時間は開始から1時間のため、今のうちに下書きを準備できます。</div>
      ${side && d.draft ? `<div class="muted small">下書きは、あなたのチームのメンバーにだけ表示されます。</div>${debateDraftBox(d, d.draft)}` : ''}
    </div>`;
  } else if (d.status === 'live' && d.current) {
    const left = `あと${remain(d.current.deadline - Date.now())}`;
    if (side === d.current.side && d.draft) {
      now = `
        <div class="debate-now mine" id="debate-now">
          <div><b>${d.current.kind}</b>・あなたのチームの番です（${left}）</div>
          ${waitInfo}
          <div class="muted small">下書きは、あなたのチームのメンバーにだけ表示されます。チームで推敲してから提出してください。</div>
          ${debateDraftBox(d, d.draft)}
          <div class="turn-actions">${waitBtn}</div>
        </div>`;
    } else {
      now = `
        <div class="debate-now">
          <div><b>${d.current.kind}</b>・「${name(d.current.side)}」の番です（${left}）</div>
          ${waitInfo}
          ${waitBtn ? `<div class="turn-actions">${waitBtn}</div>` : ''}
        </div>`;
    }
  }

  // 考えの変化
  const opt = (which: 'before' | 'after', o: string, label: string) =>
    `<button class="stance-chip ${(which === 'before' ? d.poll.before : d.poll.after) === o ? 'on' : ''}" data-debate-poll="${which}:${o}">${esc(label)}</button>`;
  const choices = (which: 'before' | 'after') => `${opt(which, 'A', d.camps.A.name)}${opt(which, 'B', d.camps.B.name)}${opt(which, 'N', 'どちらとも言えない')}`;
  let poll: string;
  if (side) poll = '<div class="muted small">討論の参加者は回答できません</div>';
  else if (!v.loggedIn && !v.guestOK) poll = '<div class="muted small">ログインすると、討論の前後で考えを回答できます</div>';
  else if (d.poll.canBefore) poll = `<div class="small">討論を読む前のあなたの考え</div><div class="stance-chips">${choices('before')}</div>`;
  else if (d.poll.canAfter) poll = `<div class="small">討論を読んだ後のあなたの考え</div><div class="stance-chips">${choices('after')}</div>`;
  else if (d.status !== 'done') poll = d.poll.before ? '<div class="muted small">討論の終了後に、もう一度考えをお聞きします</div>' : '<div class="muted small">討論前の回答は、最初の主張が提出されるまでに受け付けていました</div>';
  else poll = '<div class="muted small">討論後の回答は、討論前に回答した方のみ受け付けています</div>';
  const res = d.poll.result;
  // 終わった討論は、いちばん大きく増えた立場をひとことで出す
  const top = res ? [...res.rows].sort((x, y) => (y.after - y.before) - (x.after - x.before))[0] : null;
  const headline = !res ? '討論の前後で考えを回答した人がいなかったため、考えの変化は出ていません。'
    : top && top.after > top.before ? `「${esc(top.name)}」と答えた人の割合が、討論の前の ${top.before}% から、討論の後は <b>${top.after}%</b> に増えました。`
    : '討論の前と後で、答えの割合は変わりませんでした。';
  const summary = d.status === 'done' ? `
    <div class="debate-summary">
      <div class="debate-summary-head"><span class="st done">終了</span>この討論は終わりました</div>
      <div>${headline}</div>
    </div>` : '';
  const result = res ? `
    <div class="poll-result">
      <div class="small"><b>考えの変化</b>（討論の前後で回答した ${res.n}人）</div>
      ${res.rows.map(r => `
        <div class="poll-row">
          <div class="poll-name">${esc(r.name)}</div>
          <div class="poll-bars"><div class="poll-bar before" style="width:${r.before}%"></div><div class="poll-bar after" style="width:${r.after}%"></div></div>
          <div class="poll-nums">${r.before}% → <b>${r.after}%</b> <span class="${r.after - r.before >= 0 ? 'up' : 'down'}">（${r.after - r.before >= 0 ? '+' : ''}${r.after - r.before}）</span></div>
        </div>`).join('')}
      <div class="poll-legend"><span class="lg before"></span>討論前　<span class="lg after"></span>討論後</div>
    </div>` : '';

  // 6つの番の進み具合。いまの番を強調し、自分のチームの番なら下書きへ飛べる
  const progress = `
    <div class="debate-progress">
      <ol>${DEBATE_STEPS.map((s, i) => {
        const state = i < d.statements.length ? 'done' : d.current && d.current.step === i ? 'now' : '';
        return `<li class="${state}"><span>${s.kind}</span><b>${esc(cut(d.camps[s.side].name, 6))}</b></li>`;
      }).join('')}</ol>
      ${d.status === 'recruit' ? `<div class="progress-note">募集中：各チーム${d.capacity}人そろったら始まります</div>`
        : d.status === 'live' && d.current ? `<div class="progress-note"><b>${d.current.kind}</b>・「${name(d.current.side)}」の番（あと${remain(d.current.deadline - Date.now())}）
          ${side === d.current.side ? '<a class="draft-jump" href="#debate-now">下書きへ ↓</a>' : ''}</div>`
        : ''}
    </div>`;

  const main = `
    <div class="tl-header">
      <a href="/t/${d.topicId}" class="back-btn" aria-label="戻る">←</a>
      <div><div class="tl-title">討論場${KIND_DEBATE} 第${d.no}回<a class="help-link" href="/guide#debate" aria-label="討論場とは">？</a></div>
        <div class="tl-sub"><a href="/t/${d.topicId}">${esc(d.topicTitle)}</a></div></div>
      <span class="spacer"></span>
      ${!side && d.status !== 'done' ? `<button class="small ghost" data-debate-act="watch">${d.watching ? '通知を受け取り中（解除）' : '更新を通知する'}</button>` : ''}
    </div>
    <div class="teams">${members('A')}<div class="team-vs">と</div>${members('B')}</div>
    ${progress}
    ${summary}
    ${result}
    ${d.statements.length ? `<ol class="turns debate-turns">${statements}</ol>` : ''}
    ${now}`;

  // 作戦室：メンバーには自分のチームの分と書く欄、観客には両チームの分と書いている途中の下書き。相手チームには出さない
  const roomItem = (m: DebateView['room'][number]) => `
    <div class="seat"><div>${esc(m.text)}</div>
      <div class="seat-foot"><span class="xp-meta">${esc(m.handle)}${m.mine ? '（あなた）' : ''} · ${ago(m.at)}</span></div></div>`;
  const roomBox = side ? `
    <div class="seats room">
      <h2>作戦室</h2>
      <div class="muted small">「${name(side)}」チームのメンバーと観客に表示されます。相手チームには表示されません。</div>
      <div class="seat-list">
        ${d.room.length ? d.room.map(roomItem).join('') : '<div class="muted small">まだ書き込みはありません。方針や分担をここで相談できます</div>'}
      </div>
      <div class="seat-input room-input">
        <textarea rows="2" placeholder="チームへのメッセージ（観客にも見えます）"></textarea>
        <button class="small" data-debate-room>送信</button>
      </div>
    </div>` : (d.room.length || d.peekDrafts.length) ? `
    <div class="seats room">
      <h2>作戦室</h2>
      <div class="muted small">両チームの相談と、書いている途中の下書きです。観客にだけ公開されています。</div>
      ${(['A', 'B'] as Slot[]).map(s => {
        const msgs = d.room.filter(m => m.side === s), drafts = d.peekDrafts.filter(x => x.side === s);
        return msgs.length || drafts.length ? `
        <div class="room-team">
          <div class="room-team-name">「${name(s)}」チーム</div>
          ${drafts.map(x => `<div class="peek-draft"><div class="muted small">書いている途中の${esc(x.kind)}${x.updatedAt ? ` · ${ago(x.updatedAt)}` : ''}</div><div>${esc(x.text)}</div></div>`).join('')}
          <div class="seat-list">${msgs.map(roomItem).join('')}</div>
        </div>` : '';
      }).join('')}
    </div>` : '';
  const seats = `
    ${roomBox}
    <div class="seats" ${roomBox ? 'style="margin-top:14px"' : ''}>
      <h2>考えの変化</h2>
      ${poll}
    </div>
    <div class="seats" style="margin-top:14px">
      <h2>観客席</h2>
      <div class="seat-list">
        ${d.comments.length ? d.comments.map(c => `
          <div class="seat"><div>${esc(c.text)}</div>
            <div class="seat-foot"><span class="xp-meta">${esc(c.anon)}${c.mine ? '（あなた）' : ''} · ${ago(c.at)}</span></div></div>`).join('')
          : '<div class="muted small">まだコメントはありません</div>'}
      </div>
      ${side ? '<div class="muted small seat-note">討論の参加者は、観客席に書き込めません</div>' : `
        <div class="seat-input comment-input">
          <textarea rows="2" placeholder="コメントを書く"></textarea>
          <button class="small" data-debate-comment>コメント</button>
        </div>`}
    </div>`;

  return `
    <div class="arena-layout">
      <div class="timeline" style="view-transition-name: arena">${main}</div>
      <aside class="arena-side">${seats}</aside>
    </div>`;
}

export function addendumBox(text: string): string {
  return `
    <div class="box">
      <h3>追記する</h3>
      <p>相手が返信する前に、1回だけ追記できます。元の文は変更できません。</p>
      <div class="reply-src"><div>${esc(text)}</div></div>
      <textarea id="add-text" rows="3" placeholder="追記する内容"></textarea>
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary small" data-ok>追記する</button>
      </div>
    </div>`;
}

function postHtml(t: TopicView, p: Post, v: Viewer, ui: Ui, o: { isNew?: boolean; isReply?: boolean; hasThread?: boolean; chainAction?: string } = {}): string {
  const cls = `xpost ${o.isReply ? 'is-reply' : ''} ${o.hasThread ? 'has-thread' : ''}`;
  if (p.deleted) {
    return `<article class="${cls} deleted"><div class="xp-main"><div class="xp-body muted">この「ひとこと」は削除されました</div></div></article>`;
  }
  const open = p.campId === NONE;            // 質問・補足は誰でも反応できる
  const sameCamp = t.myCamp === p.campId;
  // 立場を決めていない人が「それな」を押すと、その立場に入る（ログインしていない人には、押すとログインを案内する）
  const canLike = !p.mine && (open || sameCamp || !t.myCamp);
  // 反対の立場の人には「それな」の代わりに「なるほど」（自分とは違うが、筋が通っている・知らなかった考え）
  const canNaruhodo = !p.mine && !open && !!t.myCamp && !sameCamp && !p.deleted;
  const canReport = !p.mine && p.modStatus !== 'violation' && p.modStatus !== 'cleared';
  // 反論：ほかの立場の人が、立場ありのひとことに1段だけ。回答：質問には誰でも
  const canReply = !o.isReply && !p.mine && (open ? p.kind === 'q' : !sameCamp);
  const replyLabel = open ? '回答する' : '反論する';
  const likeLabel = p.kind === 'q' ? '私も知りたい' : open ? '参考になった' : 'それな';
  const folded = p.folded && !ui.openFolded.has(p.id);

  let extra = '';
  if (open && p.kind === 'info') {
    extra = p.addedToPremise ? '<span class="muted small">前提に追加済み</span>'
      : `<button class="link small" data-add-premise="${p.id}">前提に追加</button>`;
  }
  return `
    <article class="${cls} ${folded ? 'folded' : ''}" data-pid="${p.id}">
      <div class="xp-main">
        ${foldNote(p, ui)}
        <div class="xp-body">${esc(p.text)}</div>
        ${sourcesHtml(p.sources)}
        <div class="xp-actions">
          <span class="xp-meta" title="${esc(p.anon)}">${esc(stanceLabel(t, p))}${p.mine ? '（あなた）' : ''} · ${ago(p.createdAt)}${o.isNew ? '<span class="new-dot" title="新着"></span>' : ''}${p.modStatus === 'cleared' && p.caseId ? ` · <a class="muted" href="/cases/${p.caseId}">判定済み</a>` : ''}</span>
          <span class="spacer"></span>
          ${canNaruhodo
            ? `<button class="act like ${p.naruhodoed ? 'on' : ''}" data-naruhodo-post="${p.id}">なるほど ${p.naruhodo}</button>`
            : canLike
            ? `<button class="act like ${p.liked ? 'on' : ''}" data-like="${p.id}">${likeLabel} ${p.likes}</button>`
            : `<span class="act static" ${p.naruhodo ? `title="反対の立場からのなるほど ${p.naruhodo}"` : ''}>${likeLabel} ${p.likes}</span>`}
          ${canReply ? `<button class="act" data-reply="${p.id}">${replyLabel}</button>` : ''}
          ${extra}
          ${o.chainAction || ''}
          ${p.mine ? `<button class="act" data-mine="${p.id}" title="メニュー">${MORE_ICON}</button>`
            : canReport ? `<button class="act" data-report="${p.id}" title="通報" ${p.reported ? 'disabled' : ''}>${MORE_ICON}</button>` : ''}
        </div>
      </div>
    </article>`;
}

// 畳まれたひとことの説明。個人情報の暴露・脅迫の通報で判定待ちのものと、違反と判定されたものは、本文を開けない
function foldNote(p: Post, ui: Ui): string {
  if (p.modStatus === 'violation') {
    return `<div class="fold-note">ルール違反と判定されたため非表示にしています
      ${p.caseId ? `<a class="link small" href="/cases/${p.caseId}">判定の詳細</a>` : ''}
      ${p.mine ? '<span class="muted small">（本文はあなたにのみ表示されています）</span>' : ''}</div>`;
  }
  if (p.urgentHidden) return '<div class="fold-note">個人情報の暴露・脅迫の通報があったため、判定が出るまで非表示にしています</div>';
  if (!p.folded) return '';
  return `<div class="fold-note">通報が集まったため折りたたんでいます（判定中）
    <button class="link small" data-unfold="${p.id}">${ui.openFolded.has(p.id) ? '隠す' : '表示'}</button></div>`;
}

// 通報の小窓。立場に関係なく誰でも通報できる
export function reportBox(p: Post): string {
  return `
    <div class="box">
      <h3>通報する</h3>
      <p class="muted small">意見への反論や批判は、通報の対象になりません。通報が一定数集まると、抽選で選ばれた利用者が、この投稿がルール違反かどうかを判定します。</p>
      <div class="reply-src"><div>${esc(cut(p.text, 120))}</div></div>
      <div class="report-reasons">
        ${(Object.entries(REPORT_REASONS) as [string, string][]).map(([k, label]) =>
          `<label class="check"><input type="radio" name="report-reason" value="${k}"> ${esc(label)}</label>`).join('')}
      </div>
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="danger small" data-ok>通報する</button>
      </div>
    </div>`;
}

// ---------- 判定 ----------
const CASE_STATUS: Record<CaseView['status'], string> = { open: '判定中', violation: 'ルール違反', cleared: '問題なし', admin: '運営が判定中' };   // 管理画面の一覧で使う
// 判定の結果は「どうなったか」で出す（話題を削除・書き込みを非表示・問題なし）
function caseOutcome(target: 'post' | 'topic', status: string, topicStatus: string | null): { label: string; cls: string } {
  if (status === 'open') return { label: '判定中', cls: 'open' };
  if (status === 'admin') return { label: '運営が判定中', cls: 'admin' };
  if (status === 'cleared') return { label: '問題なし', cls: 'cleared' };
  if (target === 'post') return { label: '書き込みを非表示', cls: 'violation' };
  if (topicStatus === 'removed') return { label: '話題を削除', cls: 'violation' };
  if (topicStatus === 'pending') return { label: '運営が確認中', cls: 'admin' };
  return { label: '話題はそのまま', cls: 'cleared' };
}
const judgedBy = (by: 'jury' | 'admin' | null, yes: number, no: number) => by === 'admin' ? '運営' : `抽選で選ばれた ${yes + no} 人・違反 ${yes}、問題なし ${no}`;
function casePage(cs: CaseView): string {
  const reason = REPORT_REASONS[cs.reason] ?? 'その他';
  const out = caseOutcome(cs.target, cs.status, cs.topicStatus);
  const what = cs.target === 'topic' ? '話題' : '書き込み';
  return single(`
    ${backHeader('判定', '', '/moderation')}
    <div class="case">
      <div class="case-summary">
        <div class="case-head"><span class="case-status ${out.cls}">${out.label}</span>${cs.kind === 'appeal' ? '<span class="kind-tag">異議</span>' : ''}</div>
        <dl class="case-dl">
          <dt>話題</dt><dd><a href="/t/${cs.topicId}">${esc(cs.topicTitle)}</a></dd>
          <dt>通報の理由</dt><dd>${esc(reason)}</dd>
          ${cs.status === 'open' ? `<dt>判定</dt><dd>${cs.voted} / 5 人</dd><dt>締め切り</dt><dd>${dateLabel(cs.deadline)}</dd>`
            : cs.status === 'admin' ? '' : `<dt>判定</dt><dd>${judgedBy(cs.decidedBy, cs.yes, cs.no)}</dd><dt>日時</dt><dd>${dateLabel(cs.decidedAt ?? cs.createdAt)}</dd>`}
        </dl>
        ${cs.target === 'topic' && cs.topicStatus === 'pending' ? '<div class="muted small">確認が終わるまで、トップと検索には出ません。</div>' : ''}
      </div>
      ${cs.text !== null ? `
        <div class="case-post">
          <div class="muted small">通報された${what}</div>
          ${cs.parentText ? `<div class="case-parent"><span class="muted small">返信先</span><div>${esc(cs.parentText)}</div></div>` : ''}
          <div class="case-text">${esc(cs.text)}</div>
          <div class="muted small">立場：${cs.target === 'topic' ? cs.topicCamps.map(esc).join('／') : esc(cs.stance)}</div>
          ${cs.statements.map(x => `<div class="case-statement"><div class="muted small">${x.side === 'author' ? (cs.target === 'topic' ? '立てた人のコメント' : '書いた人のコメント') : '通報した人のコメント'}</div><div>${esc(x.text)}</div></div>`).join('')}
        </div>`
        : cs.target === 'post' ? '<p class="notice-plain muted">書き込みの内容は公開していません。</p>' : ''}
      ${cs.canState ? `
        <div class="case-vote">
          <p class="small">${cs.canState === 'author' ? 'ルール違反ではないと考える理由など、言い分があれば書けます。' : '通報した理由をくわしく伝えたいときに書けます。'}書かなくても判定は進みます。書けるのは1回だけです。</p>
          <textarea id="case-statement" rows="3" maxlength="300" placeholder="言い分（300文字まで・任意）"></textarea>
          <div class="row"><span class="spacer"></span><button class="small" data-case-statement>言い分を送る</button></div>
        </div>` : ''}
      ${cs.canVote ? `
        <div class="case-vote">
          <p>この${cs.target === 'topic' ? '話題' : '書き込み'}が GIRONBA のルールに違反しているかを判定してください。意見の内容に賛成か反対かではなく、暴言・人格攻撃、個人情報の暴露、脅迫、差別、宣伝・荒らしに当たるかどうかで判断します。${cs.target === 'topic' ? '違反とされた話題は、運営が確認してから削除するかを決めます。' : ''}</p>
          <div class="row"><button class="danger" data-case-vote="1">違反している</button><button class="primary" data-case-vote="0">問題ない</button></div>
        </div>` : ''}
      ${cs.isJuror && cs.myVote !== null ? `<p class="muted small">あなたは「${cs.myVote ? '違反している' : '問題ない'}」と判定しました。</p>` : ''}
      ${cs.canConfirmTopic ? `
        <div class="case-vote admin-box">
          <p class="muted small">運営として、この話題の扱いを決めます</p>
          <div class="row"><button class="danger small" data-topic-remove="${cs.topicId}">削除を確定</button><button class="small" data-topic-restore="${cs.topicId}">元に戻す</button></div>
        </div>` : ''}
      ${cs.canDecideAdmin ? `
        <div class="case-vote admin-box">
          <p class="muted small">運営として判定します（陪審が集まらなかった場合や、仕組みの悪用への対応）</p>
          <div class="row"><button class="danger small" data-admin-decide="1">違反とする</button><button class="small" data-admin-decide="0">問題なしとする</button></div>
        </div>` : ''}
      ${cs.canAppeal ? `
        <div class="case-vote">
          <p>判定に納得できない場合は、1回だけ異議を申し立てられます。別の利用者が改めて判定します。</p>
          <button class="small" data-appeal="${cs.postId}">異議を申し立てる</button>
        </div>` : ''}
      <p class="small"><a href="/policy">判定の仕組みについて</a>・<a href="/moderation">判定の記録</a></p>
    </div>`);
}

function modLogPage(entries: ModLogEntry[]): string {
  return single(`
    ${backHeader('判定の記録')}
    ${entries.length ? entries.map(x => `
      <a class="inbox-row" href="/cases/${x.id}">
        <div class="inbox-where">${dateLabel(x.at)}<span class="muted">・${esc(cut(x.topicTitle, 30))}</span></div>
        <div class="inbox-text">${(o => `<span class="case-status ${o.cls}">${o.label}</span>`)(caseOutcome(x.target, x.result, x.topicStatus))}
          ${esc(REPORT_REASONS[x.reason] ?? 'その他')}${x.kind === 'appeal' ? '・異議' : ''}
          <span class="muted small">${judgedBy(x.decidedBy, x.yes, x.no)}</span></div>
        ${x.text ? `<div class="inbox-sub">${esc(x.text)}</div>` : ''}
      </a>`).join('') : '<p class="empty">まだ判定はありません</p>'}
    <p class="small notice-plain"><a href="/policy">判定の仕組み</a></p>`);
}

// ---------- 管理画面（ダッシュボード） ----------
// パソコンでは左にメニュー、右に段組みの中身。スマホではメニューが上のタブになり、中身は1列
const ADMIN_TABS: [AdminView['tab'], string][] = [['overview', '概要'], ['cases', '判定'], ['contacts', 'お問い合わせ'], ['users', '利用者'], ['invites', '招待']];
function adminPage(v: AdminView): string {
  const n = v.counts;
  const badge: Partial<Record<AdminView['tab'], number>> = { cases: n.adminCases + n.reported + n.pendingTopics, contacts: n.newContacts };
  const cur = v.tab === 'user' ? 'users' : v.tab;
  const title = v.tab === 'user' ? '利用者の詳細' : ADMIN_TABS.find(([k]) => k === v.tab)![1];
  const body = v.tab === 'overview' ? adminOverview(v.stats, n)
    : v.tab === 'cases' ? adminCases(v.cases, v.reported, v.decided, v.pendingTopics)
    : v.tab === 'contacts' ? adminContacts(v.filter, v.contacts)
    : v.tab === 'users' ? adminUsers(v.q, v.users)
    : v.tab === 'invites' ? adminInvites(v.invites)
    : adminUserDetail(v.detail);
  return `
    <div class="dash">
      <nav class="dash-nav">
        <a href="/me" class="back-btn dash-back" aria-label="戻る">← マイページ</a>
        <div class="dash-brand">管理画面</div>
        <div class="dash-links">${ADMIN_TABS.map(([k, label]) => `
          <a class="${cur === k ? 'on' : ''}" href="/admin${k === 'overview' ? '' : `?tab=${k}`}" data-replace>${label}${badge[k] ? `<i class="admin-badge">${badge[k]}</i>` : ''}</a>`).join('')}
        </div>
      </nav>
      <div class="dash-main">
        <div class="dash-head">
          ${v.tab === 'user' ? '<a href="/admin?tab=users" class="back-btn dash-sub-back" aria-label="利用者の一覧に戻る">←</a>' : ''}
          <h1>${title}</h1>
        </div>
        ${body}
      </div>
    </div>`;
}

const panel = (title: string, inner: string, extra = '') => `<section class="panel ${extra}">${title ? `<h2>${title}</h2>` : ''}${inner}</section>`;
const none = (text = 'ありません') => `<div class="panel-empty">${text}</div>`;

function kpi(label: string, value: number, sub = '', href = ''): string {
  const inner = `<div class="kpi-label">${label}</div><div class="kpi-value">${value.toLocaleString()}</div>${sub ? `<div class="kpi-sub">${sub}</div>` : ''}`;
  return href ? `<a class="kpi" href="${href}" data-replace>${inner}</a>` : `<div class="kpi">${inner}</div>`;
}

function barChart(title: string, days: { label: string; value: number }[]): string {
  const max = Math.max(1, ...days.map(d => d.value));
  return `
    <div class="chart">
      <div class="chart-title">${title}<span>計 ${days.reduce((a, d) => a + d.value, 0)}</span></div>
      <div class="chart-bars">${days.map(d => `
        <div class="chart-col" title="${d.label}：${d.value}"><span class="chart-num">${d.value || ''}</span><div class="chart-bar" style="height:${Math.round(d.value / max * 100)}%"></div></div>`).join('')}
      </div>
      <div class="chart-axis">${days.map((d, i) => `<span>${i % 2 === days.length % 2 ? d.label : ''}</span>`).join('')}</div>
    </div>`;
}

function adminOverview(s: AdminStats, n: AdminView['counts']): string {
  const todo: [string, number, string, boolean][] = [
    ['運営の判定待ち', n.adminCases, '/admin?tab=cases', true], ['話題の削除の確認待ち', n.pendingTopics, '/admin?tab=cases', true], ['未対応のお問い合わせ', n.newContacts, '/admin?tab=contacts', true],
    ['通報が付いた投稿', n.reported, '/admin?tab=cases', true], ['陪審の判定中', n.openCases, '/admin?tab=cases', false],
  ];
  return `
    <div class="kpis">
      ${kpi('登録者', s.users, `今日 +${s.usersToday}・7日 +${s.users7d}`, '/admin?tab=users')}
      ${kpi('いまオンライン', s.online, '15分以内')}
      ${kpi('今日来た人', s.activeToday, `7日以内 ${s.active7d}人`)}
      ${kpi('投稿', s.posts, `今日 +${s.postsToday}・7日 +${s.posts7d}`)}
      ${kpi('話題', s.topics, `今日 +${s.topicsToday}・7日 +${s.topics7d}`)}
      ${kpi('議論場', s.arenasOpen, `進行中・7日で${s.arenas7d}件`)}
      ${kpi('討論場', s.debatesLive, `進行中・募集中 ${s.debatesRecruit}`)}
      ${kpi('書き込み停止中', s.suspended)}
    </div>
    <div class="dash-cols">
      <div class="dash-col grow">
        ${panel('日ごとの動き（14日間）', `
          ${barChart('投稿数', s.days.map(d => ({ label: d.label, value: d.posts })))}
          ${barChart('投稿などをした人', s.days.map(d => ({ label: d.label, value: d.active })))}
          ${barChart('新しく登録した人', s.days.map(d => ({ label: d.label, value: d.signups })))}`)}
      </div>
      <div class="dash-col side">
        ${panel('対応が必要なもの', todo.map(([l, c, h, warn]) => `
          <a class="todo-row ${c && warn ? 'alert' : ''}" href="${h}" data-replace><span>${l}</span><b>${c}</b></a>`).join(''))}
        ${panel('この7日間で投稿の多い話題', s.hotTopics.map((t, i) => `
          <a class="list-row" href="/t/${t.id}"><span class="rank">${i + 1}</span><span class="grow">${esc(t.title)}</span><span class="muted small">${t.posts}件</span></a>`).join('') || none('まだありません'))}
      </div>
    </div>`;
}

const reasonLabel = (r: string) => (REPORT_REASONS as Record<string, string>)[r] ?? 'その他';

function adminCases(cases: CaseView[], reported: AdminReported[], decided: AdminDecided[], pendingTopics: Extract<AdminView, { tab: 'cases' }>['pendingTopics']): string {
  const waiting = cases.filter(c => c.status === 'admin');
  const open = cases.filter(c => c.status === 'open');
  return `
    <div class="dash-cols even">
      <div class="dash-col">
        ${pendingTopics.length ? panel(`違反と判定された話題（運営の確認待ち）<span class="count">${pendingTopics.length}</span>`, pendingTopics.map(t => `
          <div class="list-row col">
            <a href="/t/${t.topicId}">${esc(t.title)}</a>
            <div class="muted small">${esc(reasonLabel(t.reason))}・違反 ${t.yes}・問題なし ${t.no}${t.caseId ? `・<a href="/cases/${t.caseId}">判定のページ</a>` : ''}</div>
            <div class="row"><button class="small danger" data-topic-remove="${t.topicId}">削除を確定</button><button class="small" data-topic-restore="${t.topicId}">元に戻す</button></div>
          </div>`).join(''), 'alert') : ''}
        ${panel(`運営の判定待ち<span class="count">${waiting.length}</span>`, waiting.map(c => `
          <a class="list-row col" href="/cases/${c.id}"><div>${esc(cut(c.text ?? '', 90))}</div><div class="muted small">${esc(c.topicTitle)}・${esc(reasonLabel(c.reason))}${c.kind === 'appeal' ? '・異議' : ''}</div></a>`).join('') || none(), waiting.length ? 'alert' : '')}
        ${panel(`通報が付いた投稿（判定前）<span class="count">${reported.length}</span>`, `
          <div class="panel-note">通報の重みが 3 に達すると折りたたまれ、陪審の判定に回ります。</div>
          ${reported.map(r => `<div class="list-row col">
            <a href="/t/${r.topicId}">${esc(cut(r.text, 90))}</a>
            <div class="muted small">${esc(r.topicTitle)}・重み ${r.weight}${r.urgent ? `・<b class="warn">暴露・脅迫 ${r.urgent}</b>` : ''}・<a href="/admin/u/${r.authorId}">${esc(r.authorHandle)}</a>・${ago(r.createdAt)}</div>
          </div>`).join('') || none()}`)}
      </div>
      <div class="dash-col">
        ${panel(`陪審の判定中<span class="count">${open.length}</span>`, open.map(c => `
          <a class="list-row col" href="/cases/${c.id}"><div>${esc(cut(c.text ?? '', 90))}</div><div class="muted small">${esc(c.topicTitle)}・${c.voted}/${c.jurors}人が判定済み・締め切り ${dateLabel(c.deadline)}</div></a>`).join('') || none())}
        ${panel('最近の判定結果', decided.map(d => `
          <a class="list-row col" href="/cases/${d.caseId}">
            <div><span class="case-status ${d.result}">${d.result === 'violation' ? 'ルール違反' : '問題なし'}</span>${esc(cut(d.text, 70))}</div>
            <div class="muted small">${dateLabel(d.at)}・${esc(d.topicTitle)}・${esc(reasonLabel(d.reason))}${d.kind === 'appeal' ? '・異議' : ''}・${d.decidedBy === 'admin' ? '運営の判定' : `違反 ${d.yes}・問題なし ${d.no}`}</div>
          </a>`).join('') || none('まだありません'))}
      </div>
    </div>`;
}

function adminContacts(filter: 'new' | 'done', contacts: ContactView[]): string {
  return `
    <div class="seg">
      <a class="${filter === 'new' ? 'on' : ''}" href="/admin?tab=contacts" data-replace>未対応</a>
      <a class="${filter === 'done' ? 'on' : ''}" href="/admin?tab=contacts&f=done" data-replace>対応済み</a>
    </div>
    ${contacts.length ? `<div class="card-grid">${contacts.map(x => `
      <section class="panel contact-card ${x.status}">
        <div class="contact-meta"><span class="pill">${esc(x.category)}</span><span class="muted small">${dateLabel(x.createdAt)}・${esc(x.who)}${x.email ? `・返信先 ${esc(x.email)}` : ''}</span></div>
        ${x.targetUrl ? `<div class="small">対象：<a href="${esc(x.targetUrl)}" target="_blank" rel="noopener noreferrer">${esc(cut(x.targetUrl, 60))}</a></div>` : ''}
        <div class="contact-body">${esc(x.body)}</div>
        ${x.reply ? `<div class="contact-reply"><span class="muted small">返信済み</span><div>${esc(x.reply)}</div></div>` : `
          <textarea rows="3" data-reply-text="${x.id}" placeholder="返信を書く（ログイン中の人には「お知らせ」、そうでない人にはメールで届きます）"></textarea>`}
        <div class="row">
          ${x.reply ? '' : `<button class="small" data-contact-reply="${x.id}">返信する</button>`}
          <button class="small ghost" data-contact-done="${x.id}">${x.status === 'done' ? '未対応に戻す' : '対応済みにする'}</button>
        </div>
      </section>`).join('')}</div>` : panel('', none(filter === 'new' ? '未対応のお問い合わせはありません' : 'ありません'))}`;
}

function userState(u: AdminUser): string {
  if (u.status === 'suspended' && (u.suspendedUntil ?? Infinity) > Date.now()) return `<span class="pill bad">停止中（${u.suspendedUntil ? `${dateLabel(u.suspendedUntil)}まで` : '無期限'}）</span>`;
  if (u.status === 'banned') return '<span class="pill bad">利用禁止</span>';
  if (u.role === 'admin') return '<span class="pill">運営</span>';
  return '';
}

function adminUsers(q: string, users: AdminUser[]): string {
  return panel('', `
    <div class="panel-tools">
      <form class="admin-search" data-admin-search onsubmit="return false">
        <input type="search" name="q" value="${esc(q)}" placeholder="名前で検索">
        <button class="small">検索</button>
      </form>
      <span class="muted small">${q ? `「${esc(q)}」の検索結果：${users.length}人` : '最近来た順（最大100人）'}</span>
    </div>
    <div class="admin-table-wrap">
      <table class="admin-table">
        <thead><tr><th>名前</th><th>状態</th><th>登録</th><th>最後に来た</th><th class="num">投稿</th><th class="num">違反</th><th class="num">通報の信頼度</th><th class="num">判定の信頼度</th><th>招待した人</th></tr></thead>
        <tbody>${users.map(u => `
          <tr>
            <td><a href="/admin/u/${u.id}">${esc(u.handle)}</a></td>
            <td>${userState(u)}</td>
            <td>${dateLabel(u.createdAt).split(' ')[0]}</td>
            <td>${u.lastSeenAt ? ago(u.lastSeenAt) : '—'}</td>
            <td class="num">${u.posts}</td>
            <td class="num ${u.strikes ? 'warn' : ''}">${u.strikes || ''}</td>
            <td class="num">${u.reportTrust.toFixed(2)}</td>
            <td class="num">${u.jurorTrust.toFixed(2)}</td>
            <td>${u.inviter ? esc(u.inviter) : '—'}</td>
          </tr>`).join('')}
        </tbody>
      </table>
      ${users.length ? '' : none('見つかりませんでした')}
    </div>`);
}

function adminUserDetail(d: AdminUserDetail): string {
  const u = d.user;
  const suspended = u.status === 'suspended' && (u.suspendedUntil ?? Infinity) > Date.now();
  const people = (xs: { id: string; handle: string }[]) => xs.map(x => `<a href="/admin/u/${x.id}">${esc(x.handle)}</a>`).join('、');
  return `
    <div class="dash-cols">
      <div class="dash-col side">
        ${panel(`${esc(u.handle)} ${userState(u)}`, `
          <div class="kpis mini">
            ${kpi('投稿', u.posts)}${kpi('違反', u.strikes)}${kpi('通報した', d.reportsMade)}${kpi('受けた判定', d.cases.length)}
          </div>
          <dl class="admin-dl">
            <dt>登録</dt><dd>${dateLabel(u.createdAt)}</dd>
            <dt>最後に来た</dt><dd>${u.lastSeenAt ? `${dateLabel(u.lastSeenAt)}（${ago(u.lastSeenAt)}）` : '—'}</dd>
            <dt>招待コード</dt><dd>${u.invite ? `<code>${esc(u.invite)}</code>${u.inviter ? `（${esc(u.inviter)} が発行）` : ''}` : 'なし'}</dd>
            <dt>招待した人</dt><dd>${people(d.invited) || 'なし'}</dd>
            <dt>同じ回線から登録</dt><dd>${d.sameLine.length ? `<b class="warn">${people(d.sameLine)}</b>` : 'なし'}</dd>
            <dt>通報の信頼度</dt><dd>${u.reportTrust.toFixed(2)}</dd>
            <dt>判定の信頼度</dt><dd>${u.jurorTrust.toFixed(2)}</dd>
          </dl>
          ${u.role === 'admin' ? '' : `
          <div class="admin-actions">
            ${suspended ? `<button class="small" data-unsuspend="${u.id}">停止を解除</button>` : `
              <span class="muted small">書き込みを停止</span>
              <button class="small danger" data-suspend="${u.id}" data-days="1">24時間</button>
              <button class="small danger" data-suspend="${u.id}" data-days="7">7日</button>
              <button class="small danger" data-suspend="${u.id}" data-days="90">90日</button>`}
          </div>`}`)}
      </div>
      <div class="dash-col grow">
        ${panel(`受けた判定<span class="count">${d.cases.length}</span>`, d.cases.map(c => `
          <a class="list-row col" href="/cases/${c.id}"><div><span class="case-status ${c.status}">${CASE_STATUS[c.status as CaseView['status']] ?? c.status}</span>${esc(cut(c.text, 80))}</div>
            <div class="muted small">${dateLabel(c.at)}・${esc(reasonLabel(c.reason))}${c.kind === 'appeal' ? '・異議' : ''}</div></a>`).join('') || none())}
        ${panel('最近の投稿', d.posts.map(p => `
          <a class="list-row col ${p.deleted ? 'deleted' : ''}" href="/t/${p.topicId}"><div>${esc(cut(p.text, 100))}</div>
            <div class="muted small">${esc(p.topicTitle)}・${ago(p.createdAt)}${p.deleted ? '・削除済み' : ''}${p.modStatus === 'violation' ? '・違反' : p.modStatus === 'review' ? '・判定中' : ''}</div></a>`).join('') || none('まだ投稿はありません'))}
      </div>
    </div>`;
}

function adminInvites(invites: AdminInvite[]): string {
  return panel('', `
    <div class="panel-tools">
      <label class="muted small" for="invite-uses">登録できる人数</label>
      <select id="invite-uses"><option value="1">1人</option><option value="5" selected>5人</option><option value="20">20人</option><option value="0">無制限</option></select>
      <button class="small primary" data-invite-new>招待コードを発行</button>
    </div>
    <div class="admin-table-wrap">
      <table class="admin-table">
        <thead><tr><th>コード</th><th class="num">登録</th><th>発行日</th><th>このコードで登録した人</th><th></th></tr></thead>
        <tbody>${invites.map(i => `
          <tr>
            <td><code>${esc(i.code)}</code></td>
            <td class="num">${i.maxUses >= 1_000_000 ? `${i.uses}人／無制限` : `${i.uses}/${i.maxUses}`}</td>
            <td>${dateLabel(i.createdAt)}</td>
            <td class="wrap">${i.users.map(x => `<a href="/admin/u/${x.id}">${esc(x.handle)}</a>`).join('、') || '<span class="muted">—</span>'}</td>
            <td>${i.uses < i.maxUses ? `<button class="small ghost" data-copy="${esc(i.code)}">コピー</button> <button class="small ghost" data-invite-disable="${esc(i.code)}">無効にする</button>` : '<span class="muted small">使用不可</span>'}</td>
          </tr>`).join('')}
        </tbody>
      </table>
      ${invites.length ? '' : none('まだありません')}
    </div>`);
}

function contactPage(loggedIn: boolean, categories: Record<string, string>, siteKey: string): string {
  return `
    <div class="auth-page">
      <div class="auth-card">
        <h1>お問い合わせ</h1>
        <p class="auth-lead">不具合の報告、ご要望、削除の依頼などを受け付けています。</p>
        <p class="auth-note">いただいたお問い合わせのすべてに、お返事や対応ができるとは限りません。あらかじめご了承ください。</p>
        <form id="contact-form" onsubmit="return false">
          <label for="ct-category">種類</label>
          <select id="ct-category">
            <option value="">選択してください</option>
            ${Object.entries(categories).map(([k, v]) => `<option value="${k}">${esc(v)}</option>`).join('')}
          </select>
          <p class="auth-hint" id="ct-removal-hint" hidden>ご自身についての書き込みや、無断で使われた写真・文章など、権利の侵害について運営が対応します。暴言や宣伝などのルール違反は、投稿の「…」から通報してください。</p>
          <label for="ct-target">対象のページのアドレス（任意）</label>
          <input type="url" id="ct-target" placeholder="https://gironba.com/t/…">
          <label for="ct-body">内容</label>
          <textarea id="ct-body" rows="6" maxlength="3000"></textarea>
          ${loggedIn ? '<p class="auth-hint">返信は「お知らせ」に届きます。</p>' : `
            <label for="ct-email">返信先のメールアドレス（任意）</label>
            <input type="email" id="ct-email" autocomplete="email" placeholder="返信が必要な場合のみ">`}
          ${turnstileBox(siteKey)}
          <button class="primary" data-contact-send data-needs-check disabled>送信する</button>
          <p class="auth-hint">削除と開示についての考え方は「<a href="/policy">削除・開示の方針</a>」をご覧ください。裁判所・弁護士などからの正式なご連絡は contact@gironba.com でも受け付けています。</p>
        </form>
        <div id="contact-result"></div>
      </div>
    </div>`;
}

// 反論を書く小窓
export function replyBox(t: TopicView, p: Post, v: Viewer): string {
  if (p.campId === NONE) {
    return `
    <div class="box">
      <div class="reply-src">
        <div class="xp-head"><span class="xp-name">${esc(p.anon)}</span><span class="xp-time">質問</span></div>
        <div>${esc(p.text)}</div>
      </div>
      <textarea id="reply-text" rows="3" placeholder="回答を書く"></textarea>
      ${sourceInputs()}
      <div class="compose-foot">
        <span class="spacer"></span>
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary" data-ok>回答する</button>
      </div>
    </div>`;
  }
  const allowed = t.myCamp && t.myCamp !== p.campId ? t.myCamp : '';
  const camps = campOrder(t, v).filter(c => c.id !== p.campId);
  return `
    <div class="box">
      <div class="reply-src">
        <div class="xp-head"><span class="xp-name">${esc(p.anon)}</span>
          <span class="xp-time">立場：${esc(stanceLabel(t, p))}</span></div>
        <div>${esc(p.text)}</div>
      </div>
      <textarea id="reply-text" rows="3" placeholder="反論を書く"></textarea>
      ${sourceInputs()}
      <div class="compose-foot">
        <select id="reply-stance">
          ${allowed ? '' : '<option value="" selected>立場を選ぶ</option>'}
          ${camps.map(c => `<option value="${c.id}" ${allowed === c.id ? 'selected' : ''}>${esc(c.name)}</option>`).join('')}
        </select>
        <span class="spacer"></span>
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary" data-ok>反論する</button>
      </div>
    </div>`;
}

export function factBox(text: string | null, sources: string[] = []): string {
  return `
    <div class="box">
      <h3>${text !== null ? '前提を編集' : '前提を追加'}</h3>
      <p>事実だけを書きます。編集履歴に名前が残ります。${text !== null ? '変えると「異議あり」は消えます。' : ''}</p>
      <textarea id="fact-text" rows="3">${text !== null ? esc(text) : ''}</textarea>
      <div class="hint">根拠となる資料や記事のリンクがあれば付けてください。</div>
      ${sourceInputs(sources, true)}
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary small" data-ok>${text !== null ? '保存' : '追加'}</button>
      </div>
    </div>`;
}

export function tagsBox(all: string[], chosen: string[]): string {
  return `
    <div class="box">
      <h3>タグを編集</h3>
      <p>5つまで選べます。</p>
      <div class="tag-chips pick-chips">${all.map(t => `<button type="button" class="tag-chip ${chosen.includes(t) ? 'on' : ''}" data-tagpick="${esc(t)}">#${esc(t)}</button>`).join('')}</div>
      <input type="text" id="tag-extra" placeholder="その他のタグ（スペース区切り）">
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary small" data-ok>保存</button>
      </div>
    </div>`;
}

export function topicEditBox(t: TopicView): string {
  return `
    <div class="box">
      <h3>問いと立場を編集</h3>
      <p>「ひとこと」が投稿される前のみ変更できます。</p>
      <label class="muted small">問い</label>
      <input type="text" id="te-title" maxlength="${TITLE_MAX}" value="${esc(t.title)}">
      ${t.camps.map(c => `
        <label class="muted small" style="display:block;margin-top:8px">立場</label>
        <input type="text" data-camp-name="${c.id}" maxlength="${CAMP_MAX}" value="${esc(c.name)}">`).join('')}
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary small" data-ok>保存</button>
      </div>
    </div>`;
}

export function editLogBox(entries: { at: number; handle: string | null; kind: string; from: string | null; to: string | null }[]): string {
  const label: Record<string, string> = { 'premise-add': '前提を追加', 'premise-edit': '前提を編集', 'premise-del': '前提を削除', tags: 'タグを変更' };
  return `
    <div class="box">
      <h3>編集履歴</h3>
      <div class="edit-log">
        ${entries.map(l => `
          <div class="log-row">
            <div class="muted small">${dateLabel(l.at)}・${esc(l.handle || '退会した人')}・${label[l.kind] || ''}</div>
            ${l.from ? `<div class="log-from">${esc(l.from)}</div>` : ''}
            ${l.to ? `<div class="log-to">${esc(l.to)}</div>` : ''}
          </div>`).join('') || '<div class="muted small">まだありません</div>'}
      </div>
      <div class="row"><button class="ghost small" data-close>閉じる</button></div>
    </div>`;
}

export function proposalBox(t: TopicView): string {
  return `
    <div class="box">
      <h3>新しい立場を提案</h3>
      <p>あなたを含めて ${PROPOSAL_THRESHOLD} 人が参加すると、立場として加わります。</p>
      <label class="muted small">立場の名前</label>
      <input type="text" id="pr-name" maxlength="${CAMP_MAX}" placeholder="例：今は決めるべきではない">
      <label class="muted small" style="display:block;margin-top:8px">理由（任意）</label>
      <input type="text" id="pr-reason" maxlength="100">
      <label class="muted small" style="display:block;margin-top:8px">位置づけ</label>
      <select id="pr-pole">
        <option value="mid">中間・どちらでもない</option>
        ${t.camps.filter(c => c.pole === 'a' || c.pole === 'b').map(c => `<option value="lean-${c.pole}">「${esc(c.name)}」寄り</option>`).join('')}
      </select>
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="primary small" data-ok>提案する</button>
      </div>
    </div>`;
}

// 話題の「…」：タグの編集・問いと立場の編集・編集の履歴
export function topicMenuBox(t: TopicView, isAdmin = false): string {
  return `
    <div class="box">
      <h3>この話題</h3>
      <div class="share-list">
        <button class="share-item" data-menu="tags"><b>タグを編集</b><span>話題の分類を付け直します（5つまで）</span></button>
        ${t.canEdit ? '<button class="share-item" data-menu="edit"><b>問いと立場を編集</b><span>「ひとこと」が投稿される前のみ変更できます</span></button>' : ''}
        ${t.editLogCount ? '<button class="share-item" data-menu="log"><b>編集履歴</b><span>前提とタグの変更履歴</span></button>' : ''}
        ${t.canDelete ? '<button class="share-item danger-item" data-menu="delete"><b>話題を削除</b><span>ほかの人の「ひとこと」が投稿される前のみ削除できます</span></button>' : ''}
        ${!t.mine && t.modStatus !== 'cleared' && t.modStatus !== 'pending' ? (t.reportedByMe ? '<div class="share-item muted"><b>通報済み</b><span>この話題は通報済みです</span></div>'
          : '<button class="share-item" data-menu="report"><b>この話題を通報する</b><span>話題そのものがルールに違反しているとき</span></button>') : ''}
        ${!t.mine && !t.sensitivity ? (t.sensReportedByMe ? '<div class="share-item muted"><b>センシティブとして申告済み</b><span>3人集まると印が付きます</span></div>'
          : '<button class="share-item" data-menu="sens"><b>センシティブとして申告</b><span>性・暴力・事件など、伏せたほうがよい内容のとき</span></button>') : ''}
        ${isAdmin ? `<div class="share-item sens-admin"><b>印（運営）</b><span>${[0, 1, 2].map(l => `<button class="link ${t.sensitivity === l ? 'done' : ''}" data-menu="sens-set-${l}">${SENS_LABEL[l] || 'なし'}</button>`).join('')}</span></div>` : ''}
      </div>
      <div class="row"><button class="ghost small" data-close>閉じる</button></div>
    </div>`;
}

// 話題を通報する小窓
export function topicReportBox(t: TopicView): string {
  return `
    <div class="box">
      <h3>この話題を通報する</h3>
      <p class="muted small">話題そのもの（問いや立場）がルールに違反しているときに使います。中の「ひとこと」は、それぞれの「…」から通報してください。通報が一定数集まると、抽選で選ばれた利用者が判定し、違反とされた場合は運営が確認してから削除するかを決めます。</p>
      <div class="reply-src"><div>${esc(t.title)}</div></div>
      <div class="report-reasons">
        ${(Object.entries(REPORT_REASONS) as [string, string][]).map(([k, label]) =>
          `<label class="check"><input type="radio" name="report-reason" value="${k}"> ${esc(label)}</label>`).join('')}
      </div>
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="danger small" data-ok>通報する</button>
      </div>
    </div>`;
}

// 共有の小窓：X で共有／X の返信用の文をコピー／リンクをコピー
export function shareBox(t: TopicView): string {
  return `
    <div class="box">
      <h3>この話題を共有</h3>
      <p class="muted small">「${esc(cut(t.title, 40))}」</p>
      <div class="share-list">
        <button class="share-item" data-share-x><b>X で共有</b><span>話題へのリンクと #GIRONBA を付けたポストの下書きを開きます</span></button>
        <button class="share-item" data-copy-reply><b>X の返信用の文をコピー</b><span>X で議論している相手への返信に使える文をコピーします</span></button>
        <button class="share-item" data-copy-link><b>リンクをコピー</b><span>この話題のアドレスをコピーします</span></button>
      </div>
      <div class="row"><button class="ghost small" data-close>閉じる</button></div>
    </div>`;
}

// サイトそのものを共有する小窓（右の欄と一番下のリンクから）
export function siteShareBox(): string {
  return `
    <div class="box">
      <h3>GIRONBA を共有</h3>
      <div class="share-list">
        <button class="share-item" data-site-share-x><b>X で共有</b><span>GIRONBA へのリンクを付けたポストの下書きを開きます</span></button>
        <button class="share-item" data-site-share-copy><b>リンクをコピー</b><span>https://gironba.com/ をコピーします</span></button>
      </div>
      <div class="row"><button class="ghost small" data-close>閉じる</button></div>
    </div>`;
}

// 確かめる小窓（ブラウザの confirm の代わり）
export function confirmBox(title: string, body: string, ok: string, danger = false): string {
  return `
    <div class="box">
      <h3>${esc(title)}</h3>
      <p>${body}</p>
      <div class="row">
        <button class="ghost small" data-close>キャンセル</button>
        <button class="${danger ? 'danger' : 'primary'} small" data-ok>${esc(ok)}</button>
      </div>
    </div>`;
}

// ---------- お知らせ・マイページ ----------
function inboxPage(items: InboxItem[], fromMe = false): string {
  return single(`
    ${backHeader('お知らせ', '', fromMe ? '/me' : '/')}
    ${items.length ? items.map(x => `
      <a class="inbox-row ${x.unread ? 'urgent' : ''}" href="${esc(x.href)}">
        <div class="inbox-head"><span class="inbox-text">${esc(x.text)}</span><span class="inbox-time">${ago(x.at)}</span></div>
        <div class="inbox-sub">${x.where === '運営' ? '運営から' : esc(x.where)}${x.topicTitle ? `・${esc(x.topicTitle)}` : ''}${x.sub ? `　「${esc(x.sub)}」` : ''}</div>
      </a>`).join('') : '<p class="empty">いまはお知らせがありません</p>'}`);
}

// あなたの投稿：話題ごとにまとめる。見出しは話題の名前とあなたの立場、1件は「種類＋本文（1行）＋時刻」。引用の「＞」の行は外す
function myPostGroups(posts: MyPost[]): string {
  const groups = new Map<string, MyPost[]>();
  for (const p of posts) groups.set(p.topicId, [...(groups.get(p.topicId) || []), p]);
  return [...groups.values()].map(list => {
    const t = list[0];
    const camp = list.find(p => p.campName)?.campName;
    const title = `<span class="my-topic-label">話題</span>${esc(t.topicTitle)}`;
    const head = t.removed ? `<div class="my-topic">${title}<span class="muted">（削除された話題）</span></div>`
      : `<a class="my-topic" href="/t/${t.topicId}">${title} ›</a>`;
    return `<div class="my-group">${head}${camp ? `<div class="my-camp">あなたの立場：${esc(camp)}</div>` : ''}${list.map(p => {
      const text = p.text.split('\n').filter(l => !/^[>＞]/.test(l)).join(' ').trim() || p.text;
      const inner = `<span class="my-kind">${esc(p.label)}</span><span class="my-text">${esc(text)}</span><span class="my-meta">${ago(p.createdAt)}</span>`;
      return t.removed ? `<div class="my-post">${inner}</div>` : `<a class="my-post" href="${p.href}">${inner}</a>`;
    }).join('')}</div>`;
  }).join('');
}

// 参加している話題：自分が最近書いた順に5件まで。1行に話題の名前とあなたの立場
function myTopics(posts: MyPost[], limit = 5): string {
  const seen = new Map<string, MyPost[]>();
  for (const p of posts) seen.set(p.topicId, [...(seen.get(p.topicId) || []), p]);
  const rows = [...seen.values()].slice(0, limit).map(list => {
    const t = list[0], camp = list.find(p => p.campName)?.campName;
    const inner = `<span class="my-text">${esc(t.topicTitle)}${t.removed ? '<span class="muted">（削除された話題）</span>' : ''}</span>${camp ? `<span class="my-meta">${esc(camp)}</span>` : ''}`;
    return t.removed ? `<div class="my-post my-topic-row">${inner}</div>` : `<a class="my-post my-topic-row" href="/t/${t.topicId}">${inner}</a>`;
  });
  return rows.join('') || '<div class="muted small">まだ参加している話題はありません</div>';
}

function mePage(handle: string, changeableAt: number, posts: MyPost[], isAdmin: boolean, email: string | null, inbox: InboxItem[] = [], isGuest = false, trophy?: TrophySummary): string {
  const canChange = changeableAt <= Date.now();
  const card = (title: string, body: string, cls = '') => `<section class="me-card ${cls}">${title ? `<h3>${title}</h3>` : ''}${body}</section>`;
  const seg = (label: string, html: string) => `<div class="me-set"><span class="me-set-label">${label}</span>${html}</div>`;
  // 名前とメールアドレス。変えたいときだけ「変更」で入力欄を出す
  // 名前とメールアドレスは、それぞれのすぐ右に「変更」。ログアウトはカードの一番下
  const profile = card('', `<div class="prof-l">
    <div class="me-item">
      <span class="me-who">${esc(handle)}</span>
      ${canChange ? `<details class="me-edit"><summary>変更</summary>
        <div class="me-name"><input type="text" id="me-handle" maxlength="${HANDLE_MAX}" value="${esc(handle)}"><button class="small" data-me-save>保存</button></div>
        <div class="muted small">名前の変更は30日に1回までです。</div></details>`
        : `<span class="muted small">次の変更は ${dateLabel(changeableAt)} 以降</span>`}
    </div>
    <div class="muted small">名前は前提の編集履歴と討論場にだけ出ます。</div>
    <div class="me-item">
      <span>${email ? esc(email) : '<span class="muted">メールアドレス未登録</span>'}</span>
      <button class="small ghost" data-email-change>${email ? '変更' : '登録する'}</button>
    </div>
    <div class="muted small">${email ? 'ログインのメールが届くアドレスです。ほかの人には表示されません。' : '登録すると、別の端末からもログインできます。ほかの人には表示されません。'}</div>
    <div><button class="small ghost" data-logout>ログアウト</button></div></div>${trophy ? trophyMini(trophy) : ''}`, trophy ? 'prof' : '');
  const topics = card('参加している話題', `<div class="me-scroll">${myTopics(posts, 30)}</div><a class="me-more" href="/me/posts">自分の書き込みをすべて見る ›</a>`, 'me-grow');
  const news = card('最近のお知らせ', `<div class="me-scroll">${inbox.map(x => `
      <a class="me-news ${x.unread ? 'unread' : ''}" href="${esc(x.href)}"><span class="my-text">${esc(x.text)}</span><span class="my-meta">${ago(x.at)}</span></a>`).join('')
      || '<div class="muted small">いまはお知らせがありません</div>'}</div><a class="me-more" href="/inbox?from=me">お知らせをすべて見る ›</a>`, 'me-grow');
  const settings = card('設定（この端末に保存）', `
    ${seg('画面の色', themeSeg())}
    ${seg('議論場の観戦人数', '<div class="seg theme-seg" role="group" aria-label="観戦人数"><button data-watchers-set="show">表示</button><button data-watchers-set="hide">非表示</button></div>')}
    ${seg('成人向けの話題', '<div class="seg theme-seg" role="group" aria-label="成人向けの話題"><button data-adult-set="show">出す</button><button data-adult-set="hide">出さない</button></div>')}`);
  const site = card('その他', `<div class="me-links">
    <a href="/guide">使い方</a><a href="/faq">ありそうな質問</a><a href="/terms">利用規約</a><a href="/privacy">プライバシーポリシー</a>
    <a href="/policy">削除・開示の方針</a><a href="/moderation">判定の記録</a><a href="/contact">お問い合わせ</a></div>`);
  const admin = isAdmin ? card('運営メニュー', '<a class="me-more" href="/admin">管理画面（利用状況・判定・お問い合わせ・利用者・招待コード） ›</a>') : '';
  return `
    <div class="me-layout">
      ${backHeader('マイページ')}
      <div class="me-rows">
        ${isGuest ? `<section class="me-card me-guest">
          <div><b>まだ登録していません</b></div>
          <div class="small">ブラウザの履歴を消したり、別の端末を使ったりすると、このアカウントに戻れなくなります。書いた内容は残りますが、あなたのものとして扱えなくなります。メールアドレスを登録すると、ほかの端末からも使えます。</div>
          <div><button class="small primary" data-email-change>メールアドレスを登録する</button></div>
        </section>` : ''}
        ${profile}
        <div class="me-pair me-main">${topics}${news}</div>
        <div class="me-pair">${settings}<div class="me-col">${site}${admin}</div></div>
      </div>
    </div>`;
}

// ---------- トロフィーとテーマ ----------
const CUP = '<svg class="cup" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M7 3h10v2h3v3a4 4 0 0 1-4 4h-.3A5 5 0 0 1 13 14.9V18h3v3H8v-3h3v-3.1A5 5 0 0 1 8.3 12H8a4 4 0 0 1-4-4V5h3V3zm0 4H6v1a2 2 0 0 0 1.2 1.8A5 5 0 0 1 7 8.5V7zm10 0v1.5c0 .5-.1.9-.2 1.3A2 2 0 0 0 18 8V7h-1z"/></svg>';
const cup = (tier: number) => `<span class="tr-cup t${tier}">${CUP}</span>`;
const trophyNums = (t: TrophySummary) => `<span class="tr-num">トロフィー <b>${t.earned}</b> / ${t.total}</span><span class="tr-num">ポイント <b>${t.points}</b></span>`;
// マイページのプロフィールの右に出す、まとめ
function trophyMini(t: TrophySummary): string {
  const last = t.recent[0];
  return `<div class="tr-col">
    <div class="tr-row">${trophyNums(t)}</div>
    <div class="tr-recent">${t.recent.map(r => cup(r.tier)).join('')}<span class="muted small">${last ? `最近 ${esc(last.name)}・${TIER_NAME[last.tier]}` : 'まだトロフィーはありません'}</span></div>
    <a class="me-more" href="/me/trophies">トロフィーとテーマ ›</a></div>`;
}
function trophyPage(v: TrophyView, tab: 'trophy' | 'skin'): string {
  const tabs = `<div class="tr-tabs"><a href="/me/trophies" class="${tab === 'trophy' ? 'on' : ''}">トロフィー</a><a href="/me/trophies?tab=skin" class="${tab === 'skin' ? 'on' : ''}">テーマ</a></div>`;
  const items = v.items.map(i => {
    const secret = i.hidden && !i.tier;
    const right = i.tier >= 3 ? (i.at ? `${dateLabel(i.at).split(' ')[0]} 獲得` : '')
      : secret ? '' : `${i.count} / ${i.next}`;
    return `<div class="tr-item ${i.tier ? '' : 'locked'}">${cup(i.tier)}
      <div class="t"><span class="n">${secret ? '？？？' : esc(i.name)}</span><span class="d">${secret ? '隠しトロフィー' : esc(i.how)}</span></div>
      <span class="r">${right}</span></div>`;
  }).join('');
  // 行を押すと、そのテーマを試せる（保存はしない。ほかのページに移ると元に戻る）
  const skinRow = (id: string, name: string, price: number, owned: boolean) => {
    const on = (v.skin ?? '') === id;
    const btn = on ? '<span class="muted small skin-on">使用中</span>'
      : owned ? `<button class="small" data-skin-use="${id}">使う</button>`
      : `<button class="small primary" data-skin-buy="${id}" data-price="${price}" data-name="${esc(name)}" ${v.points < price ? 'disabled' : ''}>買う</button>`;
    return `<div class="tr-item skin-item ${on ? 'on' : ''}" data-skin-try="${id}">
      <div class="t"><span class="n">${esc(name)}</span><span class="d">${owned ? '所持' : `${price} ポイント`}</span></div>${btn}</div>`;
  };
  const skins = skinRow('', '標準', 0, true) + v.skins.map(s => skinRow(s.id, s.name, s.price, s.owned)).join('');
  return `
    <div class="me-layout">
      ${backHeader('トロフィーとテーマ', '', '/me')}
      <div class="me-rows">
        <div class="tr-head">${trophyNums(v)}</div>
        ${tabs}
        ${tab === 'trophy' ? `<div class="tr-grid">${items}</div>`
          : `<div class="tr-grid">${skins}</div>`}
      </div>
    </div>`;
}

// 参加する前の注意。利用規約は読まれないので、登録の入口に出す
const JOIN_NOTICE = `
  <div class="join-notice">
    <b>参加する前に</b>
    <p>GIRONBA は、異なる意見をぶつけ合う場所です。暴言や人格を攻撃する書き込みは控えてください。</p>
    <p>ただ、議論が白熱すると、強い言葉が出てしまう人もいます。厳しい反論や強い言葉を受けることがあると承知したうえで、ご参加ください。</p>
  </div>`;

// ---------- ログインと登録 ----------
// ログインと新規登録は入口を分ける（中の仕組みは同じ：メールのリンクか Google で本人を確かめる）
function loginPage(mode: 'login' | 'signup', next: string, error: string | null, googleEnabled: boolean, inviteOnly: boolean, siteKey: string): string {
  const signup = mode === 'signup';
  const q = next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
  return `
    <div class="auth-page">
      <div class="auth-card">
        <h1>${signup ? '新規登録' : 'ログイン'}</h1>
        <p class="auth-lead">${signup
          ? 'メールアドレスの確認後、名前を設定すると登録が完了します。'
          : '投稿するにはログインが必要です。閲覧はログインしなくてもできます。'}</p>
        ${signup ? JOIN_NOTICE : ''}
        ${signup && inviteOnly ? '<p class="auth-note">現在は試験公開中のため、登録には招待コードが必要です。</p>' : ''}
        ${!signup && inviteOnly ? '<p class="auth-note">招待コードで登録した方は、メールでのログインはできません。登録した端末のブラウザで、そのままご利用ください。</p>' : ''}
        ${error ? `<div class="auth-error">${esc(error)}</div>` : ''}
        ${googleEnabled ? `<a class="auth-btn" href="/auth/google?next=${encodeURIComponent(next)}">Google で${signup ? '登録' : 'ログイン'}</a>
          <div class="auth-or"><span>または</span></div>` : ''}
        <form id="email-form" onsubmit="return false">
          <label for="login-email">メールアドレス</label>
          <input type="email" id="login-email" autocomplete="email" placeholder="you@example.com">
          ${turnstileBox(siteKey)}
          <button class="primary" data-email-login data-needs-check disabled>${signup ? '確認メールを送信' : 'ログインリンクを送信'}</button>
          <p class="auth-hint">入力したメールアドレスに${signup ? '確認用' : 'ログイン用'}のリンクをお送りします。パスワードの設定は不要です。使い捨てメールアドレスはご利用いただけません。</p>
        </form>
        <div id="login-result"></div>
        <div class="auth-switch">${signup
          ? `アカウントをお持ちの方は <a href="/login${q}">ログイン</a>`
          : `アカウントをお持ちでない方は <a href="/signup${q}">新規登録</a>`}</div>
      </div>
    </div>`;
}

function welcomePage(inviteOnly: boolean, error: string | null, via: string, siteKey: string, direct: boolean): string {
  return `
    <div class="auth-page">
      <div class="auth-card">
        <h1>新規登録</h1>
        <p class="auth-lead">${direct ? '招待コードと名前を入力すると登録できます。メールアドレスは不要です。'
          : `${esc(via)}の確認が完了しました。名前を設定して登録を完了してください。`}</p>
        ${direct ? '<p class="auth-note">試験公開中は、ログインした状態がこの端末のブラウザにだけ保存されます。別の端末からはログインできません。ブラウザの履歴やデータを消すと、このアカウントには戻れなくなります。</p>' : ''}
        ${error ? `<div class="auth-error">${esc(error)}</div>` : ''}
        <form id="welcome-form" onsubmit="return false" ${direct ? 'data-direct' : ''}>
          <label for="w-handle">名前</label>
          <input type="text" id="w-handle" maxlength="${HANDLE_MAX}" placeholder="例：しろくま">
          <p class="auth-hint">「前提」の編集履歴などに表示されます。実名である必要はありません。「ひとこと」は話題ごとの匿名 ID で表示され、名前は表示されません。名前の変更は30日に1回までです。</p>
          ${inviteOnly ? `<label for="w-invite">招待コード</label>
            <input type="text" id="w-invite" autocomplete="off" placeholder="招待された方から受け取ったコード">` : ''}
          ${JOIN_NOTICE}
          <label class="check"><input type="checkbox" id="w-agree"> 上記の内容を理解し、<a href="/terms" target="_blank">利用規約</a>と<a href="/privacy" target="_blank">プライバシーポリシー</a>に同意します</label>
          ${turnstileBox(siteKey)}
          <button class="primary" data-signup data-needs-check disabled>登録する</button>
        </form>
        ${direct ? '<div class="auth-switch">アカウントをお持ちの方は <a href="/login">ログイン</a></div>' : ''}
      </div>
    </div>`;
}

function messagePage(title: string, body: string, link: string | null, linkText: string | null): string {
  return single(`
    ${backHeader(esc(title))}
    <div class="notice-plain">${esc(body)}${link ? `<p><a href="${esc(link)}">${esc(linkText || '戻る')}</a></p>` : ''}</div>`);
}

